<template>
  <!-- Email List View (Former Right Column) -->
  <div class="email-list-container">
    <div class="quick-email-title" @click="bringToFront($event)">
      <div class="title-buttons">
        <button
          type="button"
          class="title-button"
          title="New email"
          @click.stop="openComposePopup()"
        >
          <i class="fas fa-plus"></i>
        </button>
      </div>
      <span>Emails</span>
      <div class="title-actions">
        <button
          type="button"
          class="title-button"
          title="Review emails one at a time"
          @click.stop="openEmailReview"
        >
          <i class="fas fa-inbox"></i>
        </button>
        <button
          type="button"
          class="title-button"
          title="Refresh"
          @click.stop="fetchRecentCommunications"
        >
          <i class="fas fa-refresh"></i>
        </button>
        <button
          v-if="!isMobileQuickEmail"
          type="button"
          class="title-button"
          title="Close"
          @click.stop="handleCloseQuickEmails"
        >
          <i class="fas fa-times"></i>
        </button>
        <button
          type="button"
          class="home"
          title="Home"
          @click.stop="$router.push('/mhome')"
        >
          <i class="fas fa-home"></i>
        </button>
      </div>
    </div>

    <!-- Former Right Column Content -->
    <div class="email-list-content">
      <!-- Recent Communications Panel -->
      <div
        class="recent-communications"
        @mouseenter="mouseOverRecent = true"
        @mouseleave="mouseOverRecent = false"
        @keydown="handleKey"
        tabindex="0"
      >
        <div class="recent-title">
          <span
            >Recent Emails ({{ emailLimit }})
            <small>(double click to reply)</small></span
          >
        </div>

        <!-- Redesigned Search and Action Bar -->
        <div class="email-search-bar">
          <div class="search-bar-top">
            <div class="search-field">
              <input
                ref="emailFilter"
                v-model="emailFilter"
                placeholder="search: to invert use ! "
                @dblclick="clearSearchFilter"
                @focus="this.hideRead = false"
                @keydown.esc="clearSearchFilter"
              />
              <button
                type="button"
                class="search-clear"
                title="Clear search"
                @click="clearSearchFilter"
              >
                <i class="fas fa-close"></i>
              </button>
            </div>
            <div class="action-buttons-right">
              <button
                class="email-limit-increase-btn"
                @click="increaseEmailLimit"
                title="Increase email fetch limit by 500"
              >
                +500
              </button>
              <button
                type="button"
                class="email-sweep-btn"
                title="Mark all loaded emails read"
                @click="sweepUnreadEmails"
              >
                <i class="fas fa-broom"></i>
              </button>
            </div>
          </div>

          <!-- Filter Buttons Row -->
          <div class="filter-buttons-row">
            <button
              class="filter-button"
              :class="{ pushed: excludeDocusign }"
              @click="excludeDocusign = !excludeDocusign"
            >
              <i
                :class="
                  excludeDocusign ? 'fas fa-angles-down' : 'fas fa-angles-up'
                "
              ></i>
              {{ docusignLabel }}
            </button>
            <button
              class="filter-button"
              :class="{ pushed: hideRead }"
              @click="filterUnread"
            >
              <i
                :class="hideRead ? 'fas fa-angles-down' : 'fas fa-angles-up'"
              ></i>
              toggle unread
            </button>
            <button class="filter-button" @click="markAllRead">
              <i class="fas fa-check"></i> mark all read
            </button>
            <button
              class="filter-button"
              :class="{ pushed: includeDeleted }"
              @click="includeDeleted = !includeDeleted"
            >
              {{ includeDeleted ? 'Hide deleted' : 'Include deleted' }}
            </button>
            <button
              class="filter-button"
              :class="{ pushed: incomingOnly }"
              @click="incomingOnly = !incomingOnly"
            >
              Incoming Only
            </button>
          </div>
        </div>

        <!-- Loading Spinner -->
        <div v-if="isLoading" class="loading-spinner-container">
          <div class="loading-spinner"></div>
          <div class="loading-text">Loading emails...</div>
        </div>

        <!-- Review mode: single uncollapsed email (search/filters stay above) -->
        <div v-else-if="showEmailReview" class="email-review-mode">
          <div class="email-review-header">
            <span class="email-review-title">Review Emails</span>
            <span class="email-review-counter" v-if="emailReviewList.length">
              {{ emailReviewIndex + 1 }} of {{ emailReviewList.length }}
            </span>
            <span class="email-review-counter" v-else>None</span>
            <div class="email-review-header-actions">
              <button
                type="button"
                class="email-review-header-btn"
                :class="{ 'email-review-header-btn-pinned': emailReviewPinned }"
                :title="
                  emailReviewPinned
                    ? 'Unpin Review as default view'
                    : 'Pin Review as default view when opening Quick Email'
                "
                :aria-pressed="emailReviewPinned ? 'true' : 'false'"
                @click="toggleEmailReviewPin"
              >
                <i class="fas fa-thumbtack"></i>
              </button>
              <button
                type="button"
                class="email-review-header-btn"
                title="Close review"
                @click="closeEmailReview"
              >
                <i class="fas fa-times"></i>
              </button>
            </div>
          </div>

          <div
            class="email-review-body"
            @touchstart.passive="onEmailReviewTouchStart"
            @touchmove.passive="onEmailReviewTouchMove"
            @touchend.passive="onEmailReviewTouchEnd"
            @touchcancel.passive="onEmailReviewTouchCancel"
          >
            <div
              v-if="emailReviewCurrent"
              class="email-card expanded-email selected-email email-review-card"
              :class="{ 'read-email': emailReviewCurrent.read }"
              :key="emailReviewCurrent.id"
            >
              <i
                :class="[
                  'fas',
                  emailReviewCurrent.notes && emailReviewCurrent.notes.trim()
                    ? 'fa-notes-medical'
                    : 'fa-plus',
                  'email-note-icon',
                  {
                    'has-note':
                      emailReviewCurrent.notes &&
                      emailReviewCurrent.notes.trim(),
                  },
                ]"
                @click.stop="openEmailNotePopup(emailReviewCurrent)"
                :title="emailReviewCurrent.notes ? 'View note' : 'Add note'"
              ></i>
              <div class="email-card-header">
                <div
                  v-if="emailReviewCurrent.to && emailReviewCurrent.to !== ''"
                  class="email-field"
                >
                  <strong class="email-label">To:</strong>
                  <span
                    class="email-content"
                    v-html="emailReviewCurrent.to"
                  ></span>
                </div>
                <div
                  v-if="
                    emailReviewCurrent.from && emailReviewCurrent.from !== ''
                  "
                  class="email-field"
                >
                  <strong class="email-label">From:</strong>
                  <span class="email-content">
                    <span v-html="emailReviewCurrent.from"></span>
                    <span
                      v-if="getPropertyAddress(emailReviewCurrent.from)"
                      class="from-address"
                      >{{ getPropertyAddress(emailReviewCurrent.from) }}</span
                    >
                  </span>
                </div>
                <div
                  v-if="
                    emailReviewCurrent.subject &&
                    emailReviewCurrent.subject !== ''
                  "
                  class="email-field"
                >
                  <strong class="email-label">Subject:</strong>
                  <span
                    class="email-content"
                    :title="emailReviewCurrent.subject"
                    v-html="emailReviewCurrent.subject"
                  ></span>
                </div>
                <div class="email-field viewed-by-row">
                  <span class="email-label">viewed by:</span>
                  <span class="email-content">{{
                    emailReviewCurrent.readBy || '(nobody)'
                  }}</span>
                </div>
                <div class="email-tracking-info">
                  <div class="tracking-row">
                    <span
                      v-if="emailReviewCurrent.replyId"
                      class="reply-link"
                      @click.stop="toggleReplyContent(emailReviewCurrent)"
                      title="Click to view reply"
                    >
                      <i
                        :class="
                          emailReviewCurrent.showReplyContent
                            ? 'fas fa-chevron-down'
                            : 'fas fa-chevron-right'
                        "
                      ></i>
                      sent/replied by:
                    </span>
                    <span v-else class="tracking-label">sent/replied by:</span>
                    <span class="tracking-value">{{
                      emailReviewCurrent.repliedOrSentBy || '(none)'
                    }}</span>
                  </div>
                </div>
                <div class="email-icons">
                  <i
                    @click.stop="openSendToChatPicker(emailReviewCurrent)"
                    class="email-chat fas fa-comment-dots"
                    title="Send body to Inside Chat"
                  ></i>
                  <i
                    @click.stop="saveEmailToNote(emailReviewCurrent)"
                    class="email-note fas fa-sticky-note"
                    title="Save to Office Notes"
                  ></i>
                  <i
                    @click.stop="printEmail(emailReviewCurrent)"
                    class="email-print fas fa-print"
                    title="Print Email"
                  ></i>
                  <i
                    @click.stop="deleteEmail(emailReviewCurrent.id)"
                    class="email-delete fas fa-trash"
                  ></i>
                  <i
                    @click.stop="
                      handleEmailCardClick(emailReviewCurrent, $event)
                    "
                    class="email-forward fas fa-mail-reply"
                  ></i>
                </div>
              </div>

              <div
                v-if="
                  emailReviewCurrent.replyId &&
                  emailReviewCurrent.showReplyContent
                "
                class="reply-content-container"
                @mousedown.stop
                @mouseup.stop
                @click.stop
              >
                <div class="reply-content-header">
                  <strong
                    >Reply from
                    {{ emailReviewCurrent.replyEmail?.from || '...' }}</strong
                  >
                  <span class="reply-date">{{
                    emailReviewCurrent.replyEmail
                      ? formatDate(emailReviewCurrent.replyEmail.sentOn, true)
                      : ''
                  }}</span>
                </div>
                <div
                  v-if="emailReviewCurrent.replyEmail"
                  class="reply-body-content selectable-content"
                >
                  <span
                    v-html="recentBody(emailReviewCurrent.replyEmail.body, emailReviewCurrent.replyEmail.id)"
                  ></span>
                </div>
                <div v-else class="loading-reply">Loading reply...</div>
              </div>

              <div
                class="email-body-container"
                @mousedown.stop
                @mouseup.stop
                @click.stop
              >
                <div
                  class="email-body-content selectable-content"
                  @mousedown.stop
                  @mouseup.stop
                  @click.stop
                >
                  <span v-html="recentBody(emailReviewCurrent.body, emailReviewCurrent.id)"></span>
                </div>
              </div>

              <div class="email-card-footer">
                <div
                  class="email-footer-date"
                  :title="`Email ID: ${emailReviewCurrent.id}`"
                >
                  <strong>Date:</strong>
                  {{ formatDate(emailReviewCurrent.sentOn, true) }}
                </div>
                <div class="email-footer-actions">
                  <i
                    v-if="emailHasAttachments(emailReviewCurrent)"
                    class="fas fa-paperclip email-attachment-icon"
                    title="Click to open or download attachments"
                    @click.stop.prevent="
                      downloadAllAttachments(emailReviewCurrent)
                    "
                    @dblclick.stop.prevent="
                      downloadAllAttachments(emailReviewCurrent)
                    "
                  ></i>
                  <button
                    class="email-footer-forward-btn"
                    title="Forward"
                    @click.stop="openForwardPopup(emailReviewCurrent)"
                  >
                    <i class="fas fa-mail-forward"></i>
                    <span>fwd</span>
                  </button>
                </div>
              </div>
            </div>
            <div v-else class="email-review-empty">
              <i
                class="fas fa-inbox email-review-empty-icon"
                aria-hidden="true"
                title="Empty inbox"
              ></i>
              <template v-if="hideRead">
                <span
                  class="email-review-empty-text"
                  role="button"
                  tabindex="0"
                  title="Show all emails (U)"
                  @click="filterUnread"
                  @keydown.enter.prevent="filterUnread"
                >
                  No unread emails at the moment.<br />
                  Press U or click here to show all emails.
                </span>
              </template>
              <template v-else>
                <span class="email-review-empty-text">
                  No emails match the current filters.
                </span>
              </template>
            </div>
          </div>
        </div>

        <div v-else class="inner-wrapper">
          <div
            v-for="(email, index) in recentFilteredEmails"
            :key="index"
            @dblclick="toggleEmailCollapse(email.id, $event)"
            @click="
              toggleEmailCollapse(email.id, $event);
              selectEmailCard(email.id);
            "
            class="email-card"
            :title="email.collapsed ? recentBodyPlaintext(email.body) : ''"
            :class="{
              'read-email': email.read,
              'selected-email': email.id === selectedEmailId,
              'expanded-email': !email.collapsed,
            }"
          >
            <!-- Notes Icon - positioned at top right -->
            <i
              :class="[
                'fas',
                email.notes && email.notes.trim()
                  ? 'fa-notes-medical'
                  : 'fa-plus',
                'email-note-icon',
                { 'has-note': email.notes && email.notes.trim() },
              ]"
              @click.stop="openEmailNotePopup(email)"
              :title="email.notes ? 'View note' : 'Add note'"
            ></i>
            <!-- Fixed Header Information -->
            <div class="email-card-header">
              <div
                v-if="!email.collapsed && email.to && email.to !== ''"
                class="email-field"
              >
                <strong class="email-label">To:</strong>
                <span class="email-content" v-html="email.to"></span>
              </div>
              <div v-if="email.from && email.from !== ''" class="email-field">
                <strong class="email-label">From:</strong>
                <span class="email-content">
                  <span v-html="email.from"></span>
                  <span
                    v-if="getPropertyAddress(email.from)"
                    class="from-address"
                    >{{ getPropertyAddress(email.from) }}</span
                  >
                </span>
              </div>
              <div
                v-if="email.subject && email.subject !== ''"
                class="email-field"
              >
                <strong class="email-label">Subject:</strong>
                <span
                  class="email-content"
                  :title="email.subject"
                  v-html="email.subject"
                ></span>
              </div>
              <!-- Viewed by info - visible on both collapsed and expanded -->
              <div class="email-field viewed-by-row">
                <span class="email-label">viewed by:</span>
                <span class="email-content">{{
                  email.readBy || '(nobody)'
                }}</span>
              </div>
              <!-- Replied by info - Only visible when expanded -->
              <div v-if="!email.collapsed" class="email-tracking-info">
                <div class="tracking-row">
                  <span
                    v-if="email.replyId"
                    class="reply-link"
                    @click.stop="toggleReplyContent(email)"
                    title="Click to view reply"
                  >
                    <i
                      :class="
                        email.showReplyContent
                          ? 'fas fa-chevron-down'
                          : 'fas fa-chevron-right'
                      "
                    ></i>
                    sent/replied by:
                  </span>
                  <span v-else class="tracking-label">sent/replied by:</span>
                  <span class="tracking-value">{{
                    email.repliedOrSentBy || '(none)'
                  }}</span>
                </div>
              </div>
              <!-- Action Icons - Only visible when not collapsed -->
              <div v-if="!email.collapsed" class="email-icons">
                <i
                  @click.stop="openSendToChatPicker(email)"
                  class="email-chat fas fa-comment-dots"
                  title="Send body to Inside Chat"
                ></i>
                <i
                  @click.stop="saveEmailToNote(email)"
                  class="email-note fas fa-sticky-note"
                  title="Save to Office Notes"
                ></i>
                <i
                  @click.stop="printEmail(email)"
                  class="email-print fas fa-print"
                  title="Print Email"
                ></i>
                <i
                  @click.stop="deleteEmail(email.id)"
                  class="email-delete fas fa-trash"
                ></i>
                <i
                  @click.stop="handleEmailCardClick(email, $event)"
                  class="email-forward fas fa-mail-reply"
                ></i>
              </div>
            </div>

            <!-- Reply Content Section - Collapsible -->
            <div
              v-if="!email.collapsed && email.replyId && email.showReplyContent"
              class="reply-content-container"
              @mousedown.stop
              @mouseup.stop
              @click.stop
            >
              <div class="reply-content-header">
                <strong
                  >Reply from {{ email.replyEmail?.from || '...' }}</strong
                >
                <span class="reply-date">{{
                  email.replyEmail
                    ? formatDate(email.replyEmail.sentOn, true)
                    : ''
                }}</span>
              </div>
              <div
                v-if="email.replyEmail"
                class="reply-body-content selectable-content"
              >
                <span v-html="recentBody(email.replyEmail.body, email.replyEmail.id)"></span>
              </div>
              <div v-else class="loading-reply">Loading reply...</div>
            </div>

            <!-- Scrollable Body Content -->
            <div
              v-if="!email.collapsed"
              class="email-body-container"
              @mousedown.stop
              @mouseup.stop
              @click.stop
            >
              <div
                class="email-body-content selectable-content"
                @mousedown.stop
                @mouseup.stop
                @click.stop
              >
                <span v-html="recentBody(email.body, email.id)"></span>
              </div>
            </div>

            <!-- Fixed Footer Information -->
            <div class="email-card-footer">
              <div class="email-footer-date" :title="`Email ID: ${email.id}`">
                <strong>Date:</strong> {{ formatDate(email.sentOn, true)
                }}<span
                  v-if="email.collapsed && email.replyId"
                  class="replied-indicator"
                  title="Click to view reply"
                  @click.stop="openReplyFromIndicator(email, $event)"
                  >; {{ email.repliedOrSentBy || 'someone' }} replied</span
                >
              </div>
              <div class="email-footer-actions">
                <i
                  v-if="emailHasAttachments(email)"
                  class="fas fa-paperclip email-attachment-icon"
                  title="Click to open or download attachments"
                  @click.stop.prevent="downloadAllAttachments(email)"
                  @dblclick.stop.prevent="downloadAllAttachments(email)"
                ></i>
                <button
                  class="email-footer-forward-btn"
                  title="Forward"
                  @click.stop="openForwardPopup(email)"
                >
                  <i class="fas fa-mail-forward"></i>
                  <span>fwd</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Compose Email Popup (Former Left Column) -->
    <div v-if="showComposePopup" class="compose-popup" ref="composePopup">
      <div class="quick-email-title" @click="bringToFront($event)">
        <span>Send Email</span>
        <div class="title-actions">
          <i
            class="fas fa-times close-btn"
            @click.stop="showComposePopup = false"
          ></i>
          <!-- Draggable Icon -->
          <i
            :class="{ active: isDragMode }"
            ref="composePopupIcon"
            @click.stop="toggleDragMode"
          ></i>
        </div>
      </div>

      <div class="compose-content">
        <div class="form-group">
          <label for="quickEmailTo">To:</label>
          <input
            id="quickEmailTo"
            type="email"
            v-model="quickEmailTo"
            placeholder="recipient's email"
            autocomplete="off"
          />
          <label style="width: 47px">
            <input type="checkbox" v-model="includeBcc" autocomplete="off" />
            Bcc
          </label>
        </div>

        <div class="form-group">
          <label for="quickEmailSubject">Subject:</label>
          <input
            id="quickEmailSubject"
            ref="quickEmailSubject"
            type="text"
            v-model="quickEmailSubject"
            placeholder="email subject"
            autocomplete="off"
          />
        </div>

        <!-- Quick Action Buttons -->
        <div class="quick-actions-row">
          <button
            type="button"
            class="quick-action-btn"
            @click="setEmailTemplate('permission')"
          >
            Perm.
          </button>
          <button
            type="button"
            class="quick-action-btn"
            @click="setEmailTemplate('leaseEnd')"
          >
            Lease end
          </button>
          <button
            type="button"
            class="quick-action-btn"
            @click="setEmailTemplate('moveOut')"
          >
            Move Out
          </button>
          <button
            type="button"
            class="quick-action-btn"
            @click="setEmailTemplate('resignLease')"
          >
            Resign Lease
          </button>
          <button
            type="button"
            class="quick-action-btn"
            @click="setEmailTemplate('rentReminder')"
            title="Rent reminder"
          >
            rem
          </button>
          <button
            type="button"
            class="quick-action-btn"
            @click="setEmailTemplate('secDepRet')"
          >
            SecDep Ret
          </button>
        </div>

        <div class="form-group">
          <label for="quickEmailBody">Body:</label>
          <textarea
            style="margin-right: 0px"
            ref="quickEmailBody"
            id="quickEmailBody"
            v-model="quickEmailBody"
            placeholder="email body (use markdown)"
            @input="updatePreview"
            @paste="handleBodyPaste"
            autocomplete="off"
          ></textarea>
        </div>
        <div class="form-group">
          <label for="emailFooter">Footer:</label>
          <textarea
            style="margin-right: 0px"
            ref="quickEmailFooter"
            id="quickEmailFooter"
            v-model="quickEmailFooter"
            placeholder="email footer (use markdown)"
            @input="updatePreview"
            autocomplete="off"
          ></textarea>
        </div>

        <div class="form-group">
          <label for="bodyPreview">Preview:</label>
          <div
            style="user-select: all"
            v-html="compiledMarkdown"
            class="preview"
            id="bodyPreview"
          ></div>
        </div>
      </div>

      <!-- Fixed footer section -->
      <div class="compose-footer">
        <div class="form-group" style="margin-bottom: 5px">
          <label for="fileUpload">Attach:</label>
          <input
            type="file"
            id="fileUpload"
            @change="handleFileSelection"
            multiple
            accept=".pdf"
            autocomplete="off"
          />
        </div>

        <div class="form-group">
          <label for="useList">And/Or</label>
          <div id="useList">
            <button
              @click="fetchAttachmentFromList"
              class="clip-list-button"
              :class="
                useSelectedAttachments ? 'clip-use-button' : 'clip-list-button'
              "
            >
              {{
                useSelectedAttachments ? attachedCount + ' sel.' : 'use list'
              }}
            </button>
          </div>
          <div id="useClip">
            <button @click="toggleClipTool" :class="buttonClass">
              {{ buttonText }}
            </button>
            <button
              @click="fetchAttachmentFromClip"
              :class="useClip ? 'clip-use-button' : 'clip-list-button'"
            >
              {{ useClip ? 'selected' : 'use clip' }}
            </button>
          </div>
        </div>

        <!-- Action Row with Buttons and Markdown Link -->
        <div class="action-row">
          <div class="button-wrapper">
            <button
              @click="sendQuickEmail"
              :disabled="disableSendEmail"
              :class="{ 'disabled-button': disableSendEmail }"
            >
              <span v-if="!sendingEmail">Send Emails</span>
              <span v-else class="button-spinner"></span>
            </button>
            <button @click="closeComposePopup">Cancel</button>
          </div>
          <div class="markdown-help">
            <a href="https://www.markdownguide.org/cheat-sheet/" target="_blank"
              >Markdown guide</a
            >
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Rest of the popups remain unchanged -->
  <div
    ref="ledgerListPopup"
    v-if="showLedgerListPopup"
    class="ledger-list-popup"
  >
    <div class="ledger-list-title" @click="bringToFront($event)">
      Ledger List: for email attachments ({{ attachedCount }})
      <!-- Draggable Icon -->
      <i
        :class="{ active: isDragMode }"
        ref="ledgerListPopupIcon"
        @click.stop="toggleDragMode"
      ></i>
    </div>

    <button @click.prevent="toggleAll">Toggle All</button>
    <div class="ll-list-wrapper">
      <ul class="ledger-list">
        <li v-for="item in ledgerList" :key="item.id">
          <input
            style="margin-right: 3px"
            type="checkbox"
            v-model="item.attach"
          />
          <span>{{ item.docTitle }}</span>
          <span>{{ new Date(item.createdOn).toLocaleString() }}</span>
          <button @click="viewLedgerItem(item.id, item.docTitle)">
            <i class="fas fa-eye"></i>
          </button>
        </li>
      </ul>
    </div>
    <div class="button-wrapper">
      <button @click="useAttachments">use these</button>
      <button @click="showLedgerListPopup = false">close</button>
    </div>
  </div>

  <Teleport to="body">
    <div
      v-if="showPDF"
      class="attachment-viewer bring-to-frontable"
      ref="pdfViewer"
    >
      <div
        class="attachment-viewer-header"
        @mousedown="startAttachmentViewerDrag"
      >
        <button
          v-if="attachmentViewerItems.length > 1"
          type="button"
          class="attachment-viewer-nav-button"
          :disabled="attachmentViewerIndex === 0"
          title="Previous attachment"
          @mousedown.stop
          @click="showPreviousAttachment"
        >
          <i class="fas fa-chevron-left"></i>
        </button>
        <div
          v-else
          class="attachment-viewer-nav-spacer"
          aria-hidden="true"
        ></div>

        <div class="attachment-viewer-title" @click="bringToFront($event)">
          {{ viewerTitle }}
          <span
            v-if="attachmentViewerItems.length > 1"
            class="attachment-viewer-count"
          >
            ({{ attachmentViewerIndex + 1 }} /
            {{ attachmentViewerItems.length }})
          </span>
        </div>

        <div class="attachment-viewer-header-actions">
          <button
            v-if="attachmentViewerItems.length > 1"
            type="button"
            class="attachment-viewer-nav-button"
            :disabled="
              attachmentViewerIndex >= attachmentViewerItems.length - 1
            "
            title="Next attachment"
            @mousedown.stop
            @click="showNextAttachment"
          >
            <i class="fas fa-chevron-right"></i>
          </button>
          <button
            class="attachment-viewer-close-button"
            @mousedown.stop
            @click="closeAttachmentViewer"
          >
            X
          </button>
        </div>
      </div>
      <div class="attachment-viewer-body">
        <div v-if="attachmentViewerLoading" class="attachment-viewer-loading">
          <i class="fas fa-spinner fa-spin"></i>
        </div>
        <img
          v-if="viewerIsImage && pdfUrl"
          :src="pdfUrl"
          :alt="viewerTitle"
          class="attachment-viewer-image"
          @load="onAttachmentContentLoad"
        />
        <iframe
          v-else-if="pdfUrl"
          id="pdf-frame"
          :src="pdfUrl"
          frameborder="0"
          @load="onAttachmentContentLoad"
        ></iframe>
      </div>
    </div>
  </Teleport>

  <div v-if="showUrlConfirm" class="copy-panel" ref="showUrlConfirm">
    <div class="copy-panel-header">
      Link Confirmation<button
        style="
          position: absolute;
          right: 10px;
          font-size: 0.7em;
          width: 21px;
          height: 17px;
        "
        @click="closeUrlConfirm($event)"
      >
        <i class="fas fa-close"></i>
      </button>
    </div>
    Open this link? (be careful.)
    <div class="url" @click="openLink($event)">{{ docusignUrl }}</div>
  </div>

  <ClipboardToPDF v-if="showClipTool" />

  <!-- Email Notes Popup -->
  <div v-if="showEmailNotePopup" class="email-note-overlay">
    <div class="email-note-popup">
      <div class="email-note-header">
        <span>Email Note</span>
        <i
          class="fas fa-times email-note-close"
          @click="closeEmailNotePopup"
        ></i>
      </div>
      <div class="email-note-body">
        <!-- Edit Mode: Two panes -->
        <template v-if="emailNoteEditMode">
          <div class="email-note-edit-pane">
            <textarea
              ref="emailNoteTextarea"
              v-model="emailNoteContent"
              class="email-note-textarea"
              placeholder="Enter markdown notes here..."
            ></textarea>
          </div>
          <div class="email-note-preview-pane">
            <div class="email-note-preview-label">Preview:</div>
            <div
              class="email-note-preview"
              v-html="renderEmailNoteMarkdown(emailNoteContent)"
            ></div>
          </div>
        </template>
        <!-- View Mode: Just rendered preview -->
        <template v-else>
          <div class="email-note-view-pane">
            <div
              class="email-note-preview"
              v-html="renderEmailNoteMarkdown(emailNoteContent)"
            ></div>
          </div>
        </template>
      </div>
      <div class="email-note-footer">
        <button
          @click="saveEmailNote"
          class="email-note-btn"
          :disabled="!emailNoteEditMode"
        >
          Save
        </button>
        <button
          @click="editEmailNote"
          class="email-note-btn"
          :disabled="emailNoteEditMode"
        >
          Edit
        </button>
        <button
          @click="deleteEmailNote"
          class="email-note-btn"
          :disabled="!emailNoteContent || !emailNoteContent.trim()"
        >
          Delete
        </button>
        <button @click="closeEmailNotePopup" class="email-note-btn">
          Close
        </button>
      </div>
    </div>
  </div>

  <!-- Note Delete Confirmation Dialog -->
  <div v-if="showNoteDeleteConfirm" class="email-note-overlay">
    <div class="note-confirm-dialog">
      <div class="note-confirm-message">Delete this note?</div>
      <div class="note-confirm-buttons">
        <button @click="confirmDeleteNote" class="email-note-btn">OK</button>
        <button @click="cancelDeleteNote" class="email-note-btn">Cancel</button>
      </div>
    </div>
  </div>

  <!-- Send email body to Inside Chat -->
  <div
    v-if="showSendToChatPicker"
    class="email-note-overlay"
    @click.self="closeSendToChatPicker"
  >
    <div class="chat-picker-dialog" @click.stop>
      <div class="chat-picker-header">
        <span>Send to Inside Chat</span>
        <i
          class="fas fa-times email-note-close"
          @click="closeSendToChatPicker"
        ></i>
      </div>
      <input
        ref="chatPickerSearch"
        type="text"
        class="chat-picker-search"
        v-model="chatPickerQuery"
        placeholder="Search users or tenants..."
        @input="onChatPickerSearch"
      />
      <div class="chat-picker-tabs">
        <button
          type="button"
          :class="{ active: chatPickerTab === 'direct' }"
          @click="chatPickerTab = 'direct'"
        >
          Direct Chat
        </button>
        <button
          type="button"
          :class="{ active: chatPickerTab === 'group' }"
          @click="selectChatPickerGroupTab"
        >
          Group Chat
        </button>
      </div>
      <div class="chat-picker-body">
        <template v-if="chatPickerTab === 'direct'">
          <ul v-if="chatPickerResults.length">
            <li
              v-for="result in chatPickerResults"
              :key="(result.userId || '') + '-' + (result.peopleId || '')"
              @click="sendEmailBodyToChat(result)"
            >
              {{ result.displayName || result.fullName || 'Unknown' }}
              <small v-if="result.peopleId && !result.userId">tenant</small>
            </li>
          </ul>
          <div v-else-if="chatPickerQuery.trim()" class="chat-picker-empty">
            No results found
          </div>
          <div v-else class="chat-picker-empty">
            Type to search for users or tenants
          </div>
        </template>
        <template v-else>
          <div v-if="chatPickerGroupsLoading" class="chat-picker-empty">
            Loading groups...
          </div>
          <ul v-else-if="chatPickerGroupResults.length">
            <li
              v-for="group in chatPickerGroupResults"
              :key="'g-' + group.id"
              @click="
                sendEmailBodyToChat({
                  groupId: group.id,
                  groupName: group.name,
                })
              "
            >
              {{ group.name }}
            </li>
          </ul>
          <div v-else class="chat-picker-empty">No groups found</div>
        </template>
      </div>
      <div v-if="chatPickerSending" class="chat-picker-status">Sending…</div>
      <div v-if="chatPickerError" class="chat-picker-error">
        {{ chatPickerError }}
      </div>
    </div>
  </div>

  <!-- Save Email to Note Confirmation Dialog -->
  <div v-if="showSaveToNoteConfirm" class="email-note-overlay">
    <div class="note-confirm-dialog">
      <div class="note-confirm-message">Save this email to Office Notes?</div>
      <div class="note-confirm-buttons">
        <button @click="confirmSaveEmailToNote" class="email-note-btn">
          OK
        </button>
        <button @click="showSaveToNoteConfirm = false" class="email-note-btn">
          Cancel
        </button>
      </div>
    </div>
  </div>

  <!-- Forward Email Popup -->
  <div
    v-if="showForwardPopup"
    class="email-note-overlay"
    @click.self="!forwardSending && closeForwardPopup()"
  >
    <div class="forward-popup-dialog" @click.stop>
      <div class="forward-popup-title">Forward Email</div>
      <label class="forward-popup-label" for="forward-to-address"
        >Forward to:</label
      >
      <input
        id="forward-to-address"
        ref="forwardToInput"
        v-model="forwardToAddress"
        class="forward-popup-input"
        type="email"
        :disabled="forwardSending"
        @keydown.enter.prevent="sendForwardEmail"
      />
      <div v-if="forwardError" class="forward-popup-error">
        {{ forwardError }}
      </div>
      <div class="forward-popup-buttons">
        <button
          @click="sendForwardEmail"
          class="email-note-btn forward-popup-send-btn"
          :disabled="forwardSending || !forwardToAddress.trim()"
        >
          <span v-if="!forwardSending">Send</span>
          <span v-else class="forward-popup-spinner"></span>
        </button>
        <button
          @click="closeForwardPopup"
          class="email-note-btn"
          :disabled="forwardSending"
        >
          Cancel
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { marked } from 'marked';
import eventBus from './eventBus';
import axios from '@/axiosConfig';
import ClipboardToPDF from './ClipboardToPDF.vue';
import { openDB } from 'idb';
import io from 'socket.io-client';
import {
  getLastEmailId,
  setLastEmailId,
  sweepEmailReadWatermark,
  getQuickEmailLastFetchAt,
  setQuickEmailLastFetchAt,
} from '@/utils/emailLocalStorage';
import { recentEmailFetchAddresses } from '@/utils/quickEmailSearch';
import { matchBriefEmail } from '@/utils/pixieDailyEmailLink';
import {
  emailIdForDeleteKey,
  shouldStealQuickEmailShortcut,
} from '@/utils/quickEmailKeys';
import imageCompression from 'browser-image-compression';
import {
  MAX_INLINE_IMAGES,
  INLINE_UPLOAD_MAX_BYTES,
  insertAtCursor,
  referencedCids,
  nextInlineIdFromCids,
  rewriteCidImages,
  rewriteLoggedCidImages,
  pruneInlineImages,
  missingReferencedCids,
  firstClipboardImageItem,
  markdownMarker,
  canAddInlineImage,
  cidObjectUrlMap,
} from '@/utils/quickEmailInlineImages';

const QUICK_EMAIL_STALE_MS = 5 * 60 * 1000;
const EMAIL_REVIEW_PIN_KEY = 'quickemail-review-default-pinned';

function loadEmailReviewPinned() {
  try {
    return localStorage.getItem(EMAIL_REVIEW_PIN_KEY) === '1';
  } catch {
    return false;
  }
}

const QUICK_EMAIL_DRAFT_KEY = 'pixie-quickemail-compose-draft';

function loadQuickEmailDraft() {
  try {
    const raw = localStorage.getItem(QUICK_EMAIL_DRAFT_KEY);
    if (!raw) return null;
    const draft = JSON.parse(raw);
    if (!draft || typeof draft !== 'object') return null;
    const to = String(draft.to || '').trim();
    const subject = String(draft.subject || '').trim();
    const body = String(draft.body || '').trim();
    if (!to && !subject && !body) return null;
    return draft;
  } catch {
    return null;
  }
}

function saveQuickEmailDraft(payload) {
  try {
    localStorage.setItem(QUICK_EMAIL_DRAFT_KEY, JSON.stringify(payload));
  } catch {
    if (payload && payload.inlineImages) {
      try {
        const rest = { ...payload };
        delete rest.inlineImages;
        localStorage.setItem(QUICK_EMAIL_DRAFT_KEY, JSON.stringify(rest));
      } catch {
        /* ignore quota / private mode */
      }
    }
  }
}

function blobFromDataUrl(dataUrl) {
  const [header, data] = String(dataUrl || '').split(',');
  const mime = (header.match(/data:([^;]+)/) || [null, 'image/jpeg'])[1];
  const binary = atob(data || '');
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: mime });
}

function restoreDraftInlineImages(draft) {
  const rows = Array.isArray(draft && draft.inlineImages)
    ? draft.inlineImages
    : [];
  const restored = [];
  for (const row of rows) {
    if (!row || !row.cid || !row.dataUrl) continue;
    try {
      const blob = blobFromDataUrl(row.dataUrl);
      restored.push({
        cid: row.cid,
        blob,
        objectUrl: URL.createObjectURL(blob),
        mime: row.mime || blob.type || 'image/jpeg',
        dataUrl: row.dataUrl,
      });
    } catch {
      /* skip unreadable draft image */
    }
  }
  return restored;
}

function clearQuickEmailDraft() {
  try {
    localStorage.removeItem(QUICK_EMAIL_DRAFT_KEY);
  } catch {
    /* ignore */
  }
}

function saveEmailReviewPinned(pinned) {
  try {
    if (pinned) {
      localStorage.setItem(EMAIL_REVIEW_PIN_KEY, '1');
    } else {
      localStorage.removeItem(EMAIL_REVIEW_PIN_KEY);
    }
  } catch {
    /* ignore quota / private mode */
  }
}

let socket;

export default {
  components: { ClipboardToPDF },
  emits: ['showMessage', 'footerMessage', 'closeQuickEmail'],
  name: 'QuickEmail',
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    userId: {
      type: Number,
      default: null,
    },
    emailTo: {
      type: String,
      default: '',
    },
    emailSubject: {
      type: String,
      default: '',
    },
    emailBody: {
      type: String,
      default: '',
    },
  },
  data() {
    const composeDraft = loadQuickEmailDraft();
    const restoredInline = restoreDraftInlineImages(composeDraft);
    return {
      mouseOverRecent: false,
      selectedEmailId: null,
      forceShowEmailId: null,
      pendingBriefEmail: null,
      hideRead: true,
      excludeDocusign: true, // Default to excluding docusign emails
      emailDb: null,
      emailDbPromise: null,
      showUrlConfirm: false,
      docusignUrl: '',
      disableSendEmail: false,
      sendingEmail: false,
      showClipTool: false,
      showLedgerListPopup: false,
      showQuickEmails: false,
      showComposePopup: Boolean(composeDraft),
      quickEmailTo: composeDraft ? String(composeDraft.to || '') : '',
      quickEmailSubject: composeDraft
        ? String(composeDraft.subject || '')
        : '',
      quickEmailBody: composeDraft ? String(composeDraft.body || '') : '',
      quickEmailFooter:
        composeDraft && composeDraft.footer
          ? String(composeDraft.footer)
          : '<br><br>Regards,<br>**The Pixie Properties Team**<br>616-916-6465 - Julia<br>616-344-0214 - Main<br><br><small>If you are no longer a tenant of Pixie Properties please disregard this email and reply so that we can remove you from future mailings.</small>',
      markedBody: '',
      includeBcc: composeDraft ? Boolean(composeDraft.includeBcc) : false,
      composeDraftTimer: null,
      skipComposeDraftSave: Boolean(composeDraft),
      inlineImages: restoredInline,
      inlineFileKeys: {},
      nextInlineId: nextInlineIdFromCids(
        restoredInline.map((img) => img.cid),
      ),
      recentEmails: [],
      selectedFiles: [],
      useSelectedAttachments: false,
      ledgerList: [],
      isDragMode: false,
      popupPosition: { x: 300, y: 300 },
      currentPopup: null,
      currentIconOffset: { x: 0, y: 0 },
      showPDF: false,
      pdfUrl: '',
      viewerTitle: 'Attachment',
      viewerIsImage: false,
      attachmentViewerItems: [],
      attachmentViewerIndex: 0,
      attachmentViewerDragState: null,
      attachmentViewerLoading: false,
      emailFilter: '',
      incomingOnly: true,
      includeDeleted: false,
      useClip: false,
      clipBlob: null,
      collapsedEmails: true,
      isLoading: false,
      emailLimit: 700,
      templateParams: {},
      replyingToEmailId: composeDraft?.replyingToEmailId || null,
      // Email notes popup state
      showEmailNotePopup: false,
      emailNoteEditMode: false,
      emailNoteId: null,
      emailNoteContent: '',
      emailNoteOriginal: '',
      showNoteDeleteConfirm: false,
      showSaveToNoteConfirm: false,
      pendingSaveEmail: null,
      emailAddressMap: {},
      showSendToChatPicker: false,
      chatPickerEmail: null,
      chatPickerQuery: '',
      chatPickerTab: 'direct',
      chatPickerUsers: [],
      chatPickerResults: [],
      chatPickerGroups: [],
      chatPickerGroupsLoading: false,
      chatPickerSending: false,
      chatPickerError: '',
      chatFromUserId: null,
      showForwardPopup: false,
      forwardingEmail: null,
      forwardToAddress: 'scan_documents@bigpropertymanagement.com',
      forwardSending: false,
      forwardError: '',

      // Review mode (single expanded email; most recent first)
      showEmailReview: false,
      emailReviewIndex: 0,
      emailReviewPinned: loadEmailReviewPinned(),
      emailReviewTouchStartX: null,
      emailReviewTouchStartY: null,
      emailReviewTouchStartTime: 0,
      emailReviewTouchMoved: false,
    };
  },
  computed: {
    /** Standalone mobile route (/quickemail) hides the desktop close button */
    isMobileQuickEmail() {
      const path = this.$route?.path || '';
      if (path === '/quickemail') return true;
      if (typeof window !== 'undefined' && window.matchMedia) {
        return window.matchMedia('(max-width: 768px)').matches;
      }
      return false;
    },
    docusignLabel() {
      return this.excludeDocusign ? 'excl Docusign' : 'incl Docusign';
    },
    buttonText() {
      return this.showClipTool ? 'close clip' : 'get clip';
    },
    buttonClass() {
      return this.showClipTool ? 'clip-close-button' : 'clip-list-button';
    },
    attachedCount() {
      return this.ledgerList.filter((item) => item.attach).length;
    },
    recentFilteredEmails() {
      const term = this.emailFilter.toLowerCase();

      let filteredEmails = this.recentEmails;

      // Apply Docusign exclusion filter based on state variable
      if (this.excludeDocusign) {
        filteredEmails = filteredEmails.filter((email) => {
          // Only check the 'from' field for DocuSign emails
          const from = (email.from || '').toString().toLowerCase();
          return !from.includes('docusign');
        });
      }

      if (this.incomingOnly) {
        filteredEmails = filteredEmails.filter(
          (email) => email.from && !email.from.startsWith('communications@'),
        );
      }

      if (term && term !== '') {
        // Handle pipe-separated tokens as logical OR
        if (term.includes('|')) {
          const orTokens = term
            .split('|')
            .map((token) => token.trim().toLowerCase())
            .filter((token) => token !== '');

          if (orTokens.length > 0) {
            filteredEmails = filteredEmails.filter((email) => {
              const emailValues = Object.values(email).map((value) =>
                value ? value.toString().toLowerCase() : '',
              );

              // Check if ANY of the OR tokens match (logical OR)
              return orTokens.some((token) => {
                const isExclusion = token.startsWith('!');
                const actualToken = isExclusion ? token.substring(1) : token;
                const matchesToken = emailValues.some((value) =>
                  value.includes(actualToken),
                );
                return isExclusion ? !matchesToken : matchesToken;
              });
            });
          }
        } else {
          // Handle comma-separated tokens as logical AND (existing behavior for other searches)
          const tokens = term
            .split(',')
            .map((token) => token.trim().toLowerCase());

          if (tokens.length > 0 && tokens[0] !== '') {
            tokens.forEach((token) => {
              const isExclusion = token.startsWith('!');
              const actualToken = isExclusion ? token.substring(1) : token;

              filteredEmails = filteredEmails.filter((email) => {
                const emailValues = Object.values(email).map((value) =>
                  value ? value.toString().toLowerCase() : '',
                );

                const matchesToken = emailValues.some((value) =>
                  value.includes(actualToken),
                );

                return isExclusion ? !matchesToken : matchesToken;
              });
            });
          }
        }
      }

      if (this.hideRead) {
        filteredEmails = filteredEmails.filter((email) => !email.read);
      }
      if (this.forceShowEmailId != null) {
        const forced = this.recentEmails.find(
          (email) => email.id === this.forceShowEmailId,
        );
        if (
          forced &&
          !filteredEmails.some((email) => email.id === forced.id)
        ) {
          filteredEmails = [forced, ...filteredEmails];
        }
      }
      return filteredEmails.map((email) => {
        const highlightedEmail = {};

        for (const [key, value] of Object.entries(email)) {
          if (key !== 'id' && key !== 'notes') {
            highlightedEmail[key] = value ? this.highlightMatch(value) : value;
          } else {
            highlightedEmail[key] = value;
          }
        }

        return highlightedEmail;
      });
    },
    /**
     * Filtered emails for Review, most recent first (sentOn, then id).
     */
    emailReviewList() {
      return [...this.recentFilteredEmails].sort((a, b) => {
        const ta = new Date(a.sentOn || 0).getTime() || 0;
        const tb = new Date(b.sentOn || 0).getTime() || 0;
        if (tb !== ta) return tb - ta;
        return (b.id || 0) - (a.id || 0);
      });
    },
    emailReviewCurrent() {
      const list = this.emailReviewList;
      if (!list.length) return null;
      const idx = Math.min(
        Math.max(this.emailReviewIndex, 0),
        list.length - 1,
      );
      return list[idx] || null;
    },
    chatPickerGroupResults() {
      const query = this.chatPickerQuery.trim().toLowerCase();
      const groups = this.chatPickerGroups || [];
      if (!query) return groups;
      return groups.filter((group) =>
        (group.name || '').toLowerCase().includes(query),
      );
    },

    compiledMarkdown() {
      marked.setOptions({
        gfm: true,
        breaks: true,
      });
      const html = marked(this.quickEmailBody + this.quickEmailFooter);
      return rewriteCidImages(html, cidObjectUrlMap(this.inlineImages));
    },
  },
  created() {
    this.initDB();
    eventBus.on('openQuickEmail', ({ to, subject, body, template, params }) => {
      this.showComposePopup = true;
      this.quickEmailTo = to || '';
      this.quickEmailSubject = subject || '';
      this.quickEmailBody = body || '';
      this.templateParams = params || {};

      // If a template is specified, use it (params will be applied)
      if (template) {
        this.setEmailTemplate(template);
      } else {
        this.updatePreview();
      }

      // Focus the subject input after the component renders
      this.$nextTick(() => {
        if (this.$refs.quickEmailSubject) {
          this.$refs.quickEmailSubject.focus();
        }
      });
    });

    // Listen for the new search population event
    eventBus.on('populateEmailSearch', ({ emails, searchOnly, to }) => {
      // Replace commas with pipes and strip spaces for logical OR search
      const processedEmails = (emails || '')
        .split(',')
        .map((email) => email.trim())
        .filter((email) => email)
        .join('|');

      this.emailFilter = processedEmails;
      this.hideRead = false;
      this.incomingOnly = false;
      // Note: QuickEmail should already be opened by the caller before this event
      this.fetchRecentCommunications();

      // If searchOnly mode, don't open compose popup
      if (searchOnly) {
        this.showComposePopup = false;
        return;
      }

      // Search keeps every address. To: uses `to` when the caller passed it,
      // including an empty string; otherwise the full address list.
      this.quickEmailTo = to !== undefined ? to : emails;
      this.showComposePopup = true;

      // Focus the subject input after the component renders
      this.$nextTick(() => {
        if (this.$refs.quickEmailSubject) {
          this.$refs.quickEmailSubject.focus();
        }
      });
    });

    this.connectSocket();
    socket.on('newEmailMessages', (emailId) => {
      this.fetchEmailById(emailId);
    });

    eventBus.on('quickEmailMaybeRefresh', this.maybeRefreshIfStale);
  },
  mounted() {
    if (this.skipComposeDraftSave) {
      this.updatePreview();
      this.$nextTick(() => {
        this.skipComposeDraftSave = false;
      });
    } else {
      this.showComposePopup = false;
    }

    // Restore a visible search only. A blank box must not keep a prior tenant filter.
    const savedSearchPattern = localStorage.getItem('quickEmailSearchPattern');
    if (savedSearchPattern && savedSearchPattern.trim()) {
      this.emailFilter = savedSearchPattern;
    } else {
      localStorage.removeItem('quickEmailSearchPattern');
    }

    document.addEventListener(
      'visibilitychange',
      this.handleDocumentVisibilityChange,
    );
    document.addEventListener('keydown', this.handleViewedEmailDeleteKey);

    this.captureBriefEmailQuery();

    const composeTo = String(
      (this.$route && this.$route.query && this.$route.query.composeTo) || '',
    ).trim();
    if (composeTo) {
      this.showComposePopup = true;
      this.quickEmailTo = composeTo;
      this.quickEmailSubject = '';
      this.quickEmailBody = '';
      this.updatePreview();
      this.$nextTick(() => {
        if (this.$refs.quickEmailSubject) this.$refs.quickEmailSubject.focus();
      });
    }

    if (this.isPanelActive()) {
      this.maybeRefreshIfStale();
    }
    this.setupStorageListener(); // Initialize storage event listener
    eventBus.on('ClipPDFCloseDialog', this.toggleClipTool);
  },

  beforeUnmount() {
    document.removeEventListener(
      'visibilitychange',
      this.handleDocumentVisibilityChange,
    );
    document.removeEventListener('keydown', this.handleEmailReviewKeydown);
    document.removeEventListener('keydown', this.handleViewedEmailDeleteKey);
    eventBus.off('ClipPDFCloseDialog', this.toggleClipTool);
    eventBus.off('populateEmailSearch');
    eventBus.off('quickEmailMaybeRefresh', this.maybeRefreshIfStale);
    this.stopAttachmentViewerDrag();
    document.removeEventListener('keydown', this.onAttachmentViewerKeydown);
    this.revokeAttachmentViewerUrl();
    this.persistComposeDraft();
    this.clearInlineImages();
    if (socket) {
      socket.disconnect();
    }
  },
  watch: {
    includeDeleted() {
      this.fetchRecentCommunications();
    },

    '$route.query.openFrom'() {
      this.captureBriefEmailQuery();
    },

    emailFilter(newVal) {
      // Save to localStorage whenever the filter changes
      if (newVal && newVal.trim() !== '') {
        localStorage.setItem('quickEmailSearchPattern', newVal);
      } else {
        localStorage.removeItem('quickEmailSearchPattern');
      }
      this.reconcileEmailReviewIndex();
    },

    hideRead() {
      this.reconcileEmailReviewIndex();
    },
    excludeDocusign() {
      this.reconcileEmailReviewIndex();
    },
    quickEmailTo() {
      this.scheduleComposeDraftSave();
    },
    quickEmailSubject() {
      this.scheduleComposeDraftSave();
    },
    quickEmailBody() {
      this.scheduleComposeDraftSave();
    },
    quickEmailFooter() {
      this.scheduleComposeDraftSave();
    },
    includeBcc() {
      this.scheduleComposeDraftSave();
    },
    incomingOnly() {
      this.reconcileEmailReviewIndex();
    },

    show(isVisible) {
      if (isVisible) {
        this.maybeRefreshIfStale();
        this.maybeOpenPinnedEmailReview();
      }
    },

    emailReviewList: {
      handler() {
        if (this.showEmailReview) {
          this.reconcileEmailReviewIndex();
        }
      },
    },
  },
  methods: {
    captureBriefEmailQuery() {
      const query = (this.$route && this.$route.query) || {};
      const from = String(query.openFrom || '')
        .trim()
        .toLowerCase();
      if (!from) {
        this.pendingBriefEmail = null;
        return;
      }
      this.pendingBriefEmail = {
        from,
        subject: String(query.openSubject || ''),
        when: String(query.openWhen || ''),
      };
      if (this.recentEmails.length) this.applyPendingBriefEmail();
    },

    applyPendingBriefEmail() {
      const pending = this.pendingBriefEmail;
      if (!pending) return;
      this.pendingBriefEmail = null;
      const match = matchBriefEmail(this.recentEmails, pending);
      if (!match) {
        if (this.showEmailReview) this.closeEmailReview();
        this.hideRead = false;
        this.emailFilter = pending.from;
        return;
      }
      if (this.showEmailReview) this.closeEmailReview();
      this.forceShowEmailId = match.id;
      const row = this.recentEmails.find((email) => email.id === match.id);
      if (row) {
        row.collapsed = false;
        this.selectedEmailId = row.id;
        this.trackEmailView(row);
      }
      this.$nextTick(() => {
        const card = this.$el && this.$el.querySelector('.selected-email');
        if (card && card.scrollIntoView) {
          card.scrollIntoView({ block: 'nearest' });
        }
      });
    },

    isPanelActive() {
      return this.$route?.path === '/quickemail' || this.show;
    },

    maybeRefreshIfStale() {
      if (!this.isPanelActive() || this.isLoading) {
        return;
      }

      const lastFetchAt = getQuickEmailLastFetchAt(this.userId);
      const isStale =
        !lastFetchAt || Date.now() - lastFetchAt > QUICK_EMAIL_STALE_MS;
      // After a full page reload the list is empty but lastFetchAt may still be fresh.
      const needsInitialLoad = this.recentEmails.length === 0;

      if (isStale || needsInitialLoad) {
        this.fetchRecentCommunications();
      } else {
        this.maybeOpenPinnedEmailReview();
      }
    },

    toggleEmailReviewPin() {
      this.emailReviewPinned = !this.emailReviewPinned;
      saveEmailReviewPinned(this.emailReviewPinned);
      const msg = this.emailReviewPinned
        ? 'Review is now the default view'
        : 'Review is no longer the default view';
      this.$emit('showMessage', msg);
      if (typeof this.updateFooterMessage === 'function') {
        this.updateFooterMessage(msg);
      }
    },

    maybeOpenPinnedEmailReview() {
      if (!this.emailReviewPinned || this.showEmailReview) return;
      if (!this.isPanelActive()) return;
      // Always open review when pinned (even with zero matches / all read).
      this.openEmailReview();
    },

    openEmailReview() {
      const list = this.emailReviewList;
      this.emailReviewIndex = 0;
      this.showEmailReview = true;
      this.selectedEmailId = list[0]?.id ?? null;
      document.addEventListener('keydown', this.handleEmailReviewKeydown);
      this.$nextTick(() => {
        if (list.length) {
          this.activateEmailReviewCurrent();
        }
      });
      return true;
    },

    closeEmailReview() {
      document.removeEventListener('keydown', this.handleEmailReviewKeydown);
      // Mark current as read when leaving review (same idea as collapsing)
      const cur = this.emailReviewCurrent;
      if (cur?.id) {
        const orig = this.recentEmails.find((e) => e.id === cur.id);
        if (orig && !orig.read) {
          orig.read = true;
          this.markEmailAsRead(orig.id, true);
        }
        if (orig) orig.collapsed = true;
      }
      this.showEmailReview = false;
      this.emailReviewTouchStartX = null;
      this.emailReviewTouchStartY = null;
      this.emailReviewTouchStartTime = 0;
      this.emailReviewTouchMoved = false;
    },

    reconcileEmailReviewIndex() {
      if (!this.showEmailReview) return;
      const list = this.emailReviewList;
      if (!list.length) {
        this.emailReviewIndex = 0;
        return;
      }
      const currentId = this.selectedEmailId;
      if (currentId != null) {
        const idx = list.findIndex((e) => e.id === currentId);
        if (idx >= 0) {
          this.emailReviewIndex = idx;
          return;
        }
      }
      this.emailReviewIndex = Math.min(this.emailReviewIndex, list.length - 1);
      this.selectedEmailId = list[this.emailReviewIndex]?.id ?? null;
      this.$nextTick(() => this.activateEmailReviewCurrent());
    },

    async activateEmailReviewCurrent() {
      const cur = this.emailReviewCurrent;
      if (!cur?.id) return;
      this.selectedEmailId = cur.id;
      const orig = this.recentEmails.find((e) => e.id === cur.id);
      if (!orig) return;
      orig.collapsed = false;
      try {
        await this.trackEmailView(orig);
      } catch (_) {
        /* ignore */
      }
    },

    async goEmailReviewIndex(nextIndex) {
      const list = this.emailReviewList;
      if (!list.length) return;
      const max = list.length - 1;
      const clamped = Math.min(Math.max(nextIndex, 0), max);
      if (clamped === this.emailReviewIndex) return;

      // Leaving an email: mark read if unread (matches collapse behavior)
      const prev = this.emailReviewCurrent;
      if (prev?.id) {
        const prevOrig = this.recentEmails.find((e) => e.id === prev.id);
        if (prevOrig) {
          if (!prevOrig.read) {
            prevOrig.read = true;
            try {
              await this.markEmailAsRead(prevOrig.id, true);
            } catch (_) {
              /* ignore */
            }
          }
          prevOrig.collapsed = true;
        }
      }

      this.emailReviewIndex = clamped;
      this.selectedEmailId = list[clamped]?.id ?? null;
      await this.activateEmailReviewCurrent();
    },

    emailReviewPrev() {
      if (this.emailReviewIndex > 0) {
        this.goEmailReviewIndex(this.emailReviewIndex - 1);
      }
    },

    emailReviewNext() {
      if (this.emailReviewIndex < this.emailReviewList.length - 1) {
        this.goEmailReviewIndex(this.emailReviewIndex + 1);
      }
    },

    /**
     * Review mode: mark the current email read (keyboard "m").
     * With toggle-unread on, the email drops out of emailReviewList and the
     * next remaining item (or empty placeholder) is shown.
     */
    async markEmailReviewCurrentAsRead() {
      const cur = this.emailReviewCurrent;
      if (!cur?.id) return;

      const orig = this.recentEmails.find((e) => e.id === cur.id);
      if (!orig) return;

      // Already read: still collapse; hideRead will drop it if that filter is on
      orig.read = true;
      orig.collapsed = true;
      try {
        await this.markEmailAsRead(orig.id, true);
      } catch (e) {
        console.error('Error marking review email read:', e);
      }

      // Reconcile after Vue re-computes emailReviewList (hideRead filters read out)
      this.$nextTick(() => {
        this.reconcileEmailReviewIndex();
      });
    },

    viewedEmailId() {
      if (this.showEmailReview) {
        return this.emailReviewCurrent ? this.emailReviewCurrent.id : null;
      }
      const email = (this.recentEmails || []).find(
        (row) => row.id === this.selectedEmailId,
      );
      if (!email || email.collapsed) return null;
      return email.id;
    },
    quickEmailDialogOpen() {
      return (
        this.showComposePopup ||
        this.showEmailNotePopup ||
        this.showForwardPopup ||
        this.showPDF ||
        this.showLedgerListPopup ||
        this.showUrlConfirm ||
        this.showSaveToNoteConfirm ||
        this.showNoteDeleteConfirm ||
        this.showSendToChatPicker ||
        this.showClipTool
      );
    },
    handleViewedEmailDeleteKey(event) {
      const id = emailIdForDeleteKey({
        key: event.key,
        pointerOverPanel: this.mouseOverRecent,
        stealShortcut: shouldStealQuickEmailShortcut(event),
        dialogOpen: this.quickEmailDialogOpen(),
        emailId: this.viewedEmailId(),
      });
      if (!id) return;
      event.preventDefault();
      event.stopPropagation();
      this.deleteEmail(id);
    },
    handleEmailReviewKeydown(event) {
      if (!this.showEmailReview) return;

      const key = event.key;
      const isArrow = key === 'ArrowLeft' || key === 'ArrowRight';
      const isMarkRead = key === 'm' || key === 'M';
      const isToggleUnread = key === 'u' || key === 'U';
      if (!isArrow && !isMarkRead && !isToggleUnread) return;

      // Don't steal keys while compose / notes / forward / other dialogs are open
      if (
        this.showComposePopup ||
        this.showEmailNotePopup ||
        this.showForwardPopup ||
        this.showPDF ||
        this.showLedgerListPopup ||
        this.showUrlConfirm ||
        this.showSaveToNoteConfirm ||
        this.showNoteDeleteConfirm
      ) {
        return;
      }

      const el = event.target;
      // Don't steal keys while typing in the search box or other fields.
      if (!shouldStealQuickEmailShortcut(event)) {
        return;
      }

      // Only handle when Quick Email owns the pointer or keyboard focus so we
      // don't fight MWorkOrders Review (document-level listeners).
      const qeRoot = document.querySelector('.email-list-container');
      const pointerOverQE = Boolean(
        document.querySelector('.email-list-container:hover'),
      );
      const focusInQE = Boolean(
        el && qeRoot && typeof el.closest === 'function'
          ? el.closest('.email-list-container')
          : false,
      );
      if (!pointerOverQE && !focusInQE && !isToggleUnread) {
        return;
      }
      if (document.querySelector('.review-fullscreen:hover')) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      if (isToggleUnread) {
        this.filterUnread();
        return;
      }
      if (isMarkRead) {
        this.markEmailReviewCurrentAsRead();
        return;
      }
      if (key === 'ArrowLeft') {
        this.emailReviewPrev();
      } else {
        this.emailReviewNext();
      }
    },

    onEmailReviewTouchStart(event) {
      if (!event.touches || event.touches.length !== 1) return;
      const t = event.touches[0];
      this.emailReviewTouchStartX = t.clientX;
      this.emailReviewTouchStartY = t.clientY;
      this.emailReviewTouchStartTime = Date.now();
      this.emailReviewTouchMoved = false;
    },

    onEmailReviewTouchMove(event) {
      if (this.emailReviewTouchStartX == null || !event.touches?.length) return;
      const t = event.touches[0];
      const dx = Math.abs(t.clientX - this.emailReviewTouchStartX);
      const dy = Math.abs(t.clientY - this.emailReviewTouchStartY);
      if (dx > 12 || dy > 12) {
        this.emailReviewTouchMoved = true;
      }
    },

    onEmailReviewTouchCancel() {
      this.emailReviewTouchStartX = null;
      this.emailReviewTouchStartY = null;
      this.emailReviewTouchStartTime = 0;
      this.emailReviewTouchMoved = false;
    },

    onEmailReviewTouchEnd(event) {
      if (this.emailReviewTouchStartX == null || this.emailReviewTouchStartY == null) {
        return;
      }
      const touch =
        (event.changedTouches && event.changedTouches[0]) || null;
      if (!touch) {
        this.onEmailReviewTouchCancel();
        return;
      }

      const dx = touch.clientX - this.emailReviewTouchStartX;
      const dy = touch.clientY - this.emailReviewTouchStartY;
      const elapsed = Date.now() - this.emailReviewTouchStartTime;
      this.onEmailReviewTouchCancel();

      const minDistance = 50;
      const maxDuration = 800;
      const absX = Math.abs(dx);
      const absY = Math.abs(dy);

      // Horizontal swipe only (allow body scroll vertically)
      if (elapsed > maxDuration || absX < minDistance || absX < absY * 1.4) {
        return;
      }

      if (dx < 0) {
        this.emailReviewNext(); // swipe left → next (older)
      } else {
        this.emailReviewPrev(); // swipe right → prev (newer)
      }
    },

    handleDocumentVisibilityChange() {
      if (!document.hidden) {
        this.maybeRefreshIfStale();
      }
    },

    acknowledgeFetchedEmails(emails) {
      if (!emails.length) {
        return;
      }

      const maxEmailId = Math.max(...emails.map((email) => email.id));
      const currentLastEmailId = parseInt(
        getLastEmailId(this.userId),
        10,
      );
      if (maxEmailId > currentLastEmailId) {
        setLastEmailId(this.userId, maxEmailId);
        eventBus.emit('emailsUnseenCountChanged');
      }
    },

    // Function to get auth token from localStorage
    getAuthToken() {
      return localStorage.getItem('token');
    },

    queueInlineFileKeys(emailId, cids) {
      if (!this._inlineKeyQueue) this._inlineKeyQueue = [];
      if (!this._inlineKeyPending) this._inlineKeyPending = Object.create(null);
      if (!this._inlineKeyFailed) this._inlineKeyFailed = Object.create(null);
      let added = false;
      for (const cid of cids || []) {
        const cacheKey = `${emailId}:${cid}`;
        if (
          this.inlineFileKeys[cacheKey] ||
          this._inlineKeyPending[cacheKey] ||
          this._inlineKeyFailed[cacheKey]
        ) {
          continue;
        }
        this._inlineKeyPending[cacheKey] = true;
        this._inlineKeyQueue.push({
          cacheKey,
          path: `/api/sent-email-inline/${emailId}/${encodeURIComponent(cid)}`,
        });
        added = true;
      }
      if (!added || this._inlineKeyTimer) return;
      this._inlineKeyTimer = setTimeout(() => {
        this._inlineKeyTimer = null;
        this.flushInlineFileKeys();
      }, 30);
    },

    async flushInlineFileKeys() {
      const batch = (this._inlineKeyQueue || []).splice(0, 20);
      if ((this._inlineKeyQueue || []).length) {
        this._inlineKeyTimer = setTimeout(() => {
          this._inlineKeyTimer = null;
          this.flushInlineFileKeys();
        }, 0);
      }
      if (!batch.length) return;
      try {
        const { data } = await axios.post('/api/file-access-keys', {
          paths: batch.map((item) => item.path),
        });
        const byPath = {};
        for (const item of (data && data.keys) || []) {
          if (item && item.path && item.key) byPath[item.path] = item.key;
        }
        const next = { ...this.inlineFileKeys };
        for (const item of batch) {
          const key = byPath[item.path];
          if (key) next[item.cacheKey] = key;
          delete this._inlineKeyPending[item.cacheKey];
        }
        this.inlineFileKeys = next;
      } catch (error) {
        if (!this._inlineKeyFailed) this._inlineKeyFailed = Object.create(null);
        for (const item of batch) {
          delete this._inlineKeyPending[item.cacheKey];
          this._inlineKeyFailed[item.cacheKey] = true;
        }
      }
    },

    async requestFileAccessKey(pathname) {
      const { data } = await axios.post('/api/file-access-keys', {
        paths: [pathname],
      });
      const hit = ((data && data.keys) || []).find(
        (item) => item.path === pathname,
      );
      if (!hit || !hit.key) throw new Error('No file key');
      return hit.key;
    },

    // ========== EMAIL NOTES METHODS ==========
    openEmailNotePopup(email) {
      this.emailNoteId = email.id;
      this.emailNoteContent = email.notes || '';
      this.emailNoteOriginal = email.notes || '';
      this.showEmailNotePopup = true;
      // If note exists, show view mode first; otherwise show edit mode
      this.emailNoteEditMode = !email.notes || !email.notes.trim();
      // Focus textarea if in edit mode
      if (this.emailNoteEditMode) {
        this.$nextTick(() => {
          if (this.$refs.emailNoteTextarea) {
            this.$refs.emailNoteTextarea.focus();
          }
        });
      }
    },

    closeEmailNotePopup() {
      this.showEmailNotePopup = false;
      this.emailNoteEditMode = false;
      this.emailNoteId = null;
      this.emailNoteContent = '';
      this.emailNoteOriginal = '';
    },

    editEmailNote() {
      this.emailNoteEditMode = true;
      this.$nextTick(() => {
        if (this.$refs.emailNoteTextarea) {
          this.$refs.emailNoteTextarea.focus();
        }
      });
    },

    async saveEmailNote() {
      if (!this.emailNoteId) return;

      try {
        const response = await axios.put(
          `/api/email-note/${this.emailNoteId}`,
          {
            notes: this.emailNoteContent,
          },
        );

        if (response.data.success) {
          // Update the local email record
          const email = this.recentEmails.find(
            (e) => e.id === this.emailNoteId,
          );
          if (email) {
            email.notes = this.emailNoteContent;
          }
          this.emailNoteOriginal = this.emailNoteContent;
          this.emailNoteEditMode = false;
          this.showEmailNotePopup = false;
        }
      } catch (error) {
        console.error('Error saving email note:', error);
        alert('Failed to save note');
      }
    },

    deleteEmailNote() {
      if (!this.emailNoteId) return;
      this.showNoteDeleteConfirm = true;
    },

    cancelDeleteNote() {
      this.showNoteDeleteConfirm = false;
    },

    async confirmDeleteNote() {
      this.showNoteDeleteConfirm = false;
      try {
        const response = await axios.delete(
          `/api/email-note/${this.emailNoteId}`,
        );

        if (response.data.success) {
          // Update the local email record
          const email = this.recentEmails.find(
            (e) => e.id === this.emailNoteId,
          );
          if (email) {
            email.notes = '';
          }
          this.closeEmailNotePopup();
        }
      } catch (error) {
        console.error('Error deleting email note:', error);
      }
    },

    renderEmailNoteMarkdown(text) {
      if (!text) return '';
      try {
        marked.setOptions({
          gfm: true,
          breaks: true,
          sanitize: false,
        });
        return marked.parse(text);
      } catch (error) {
        console.error('Error rendering markdown:', error);
        return text;
      }
    },

    clearSearchFilter() {
      this.emailFilter = '';
      localStorage.removeItem('quickEmailSearchPattern');
      // If emails were pre-filtered (e.g. from tenant CTRL-dblclick), reset and refetch all
      if (this.quickEmailTo) {
        this.quickEmailTo = '';
        this.fetchRecentCommunications();
      }
    },

    connectSocket() {
      try {
        const socketUrl = process.env.VUE_APP_SOCKET_URL;

        if (!socketUrl) {
          console.error('Socket URL is undefined.');
          return;
        }

        const token = this.getAuthToken();

        socket = io(socketUrl, {
          query: {
            token: token,
            source: 'quickEmail',
          },
        });

        socket.on('connect', () => {
          console.log('QuickEmail: Connected to WebSocket server');
        });

        socket.on('disconnect', () => {
          console.log('Disconnected from WebSocket server');
        });
      } catch (error) {
        console.error('Error connecting to WebSocket server:', error);
      }
    },

    handleCloseQuickEmails() {
      console.log('close');
      eventBus.emit('closeQuickEmails');
    },
    markAllRead() {
      this.recentFilteredEmails.forEach((email) => {
        const original = this.recentEmails.find((e) => e.id === email.id);
        if (original && !original.read) {
          original.read = true;
          this.markEmailAsRead(email.id);
        }
      });
    },
    async sweepUnreadEmails() {
      const emails = this.recentEmails || [];
      emails.forEach((email) => {
        email.read = true;
      });
      sweepEmailReadWatermark(
        this.userId,
        emails.map((email) => email.id),
      );
      eventBus.emit('emailsUnseenCountChanged');
      const db = await this.initDB();
      if (!db || typeof db.transaction !== 'function' || !emails.length) {
        return;
      }
      const tx = db.transaction('emails', 'readwrite');
      const store = tx.objectStore('emails');
      const writes = emails.map((email) =>
        store.put({ id: email.id, read: true }),
      );
      await Promise.all(writes);
      await tx.done;
    },
    async handleKey(event) {
      // Letter shortcuts must not apply while typing in the search input.
      if (!shouldStealQuickEmailShortcut(event)) {
        return;
      }

      // Review empty-unread: 'u' must work without mouseenter
      if (
        (event.key === 'u' || event.key === 'U') &&
        (this.mouseOverRecent || this.showEmailReview)
      ) {
        event.preventDefault();
        this.filterUnread();
        return;
      }

      if (this.mouseOverRecent) {

        if (event.key === 'm' && this.selectedEmailId) {
          // Find in recentEmails (not filtered) to ensure we modify the source
          const email = this.recentEmails.find(
            (em) => em.id === this.selectedEmailId,
          );
          if (email) {
            if (!email.collapsed) {
              // If expanded, collapse and mark as unread
              email.collapsed = true;
              email.read = false;
              this.markEmailAsRead(email.id, false);
            } else if (!email.read) {
              // If collapsed and unread, mark as read
              email.read = true;
              this.markEmailAsRead(email.id, true);
            } else {
              // If collapsed and read, mark as unread
              email.read = false;
              this.markEmailAsRead(email.id, false);
            }
            this.selectedEmailId = null;
          }
        } else if (event.key === 'c' && this.selectedEmailId) {
          // Collapse all emails
          this.recentEmails.forEach((email) => {
            email.collapsed = true;
          });
          // Keep the current email selected
          const selectedEmail = this.recentFilteredEmails.find(
            (em) => em.id === this.selectedEmailId,
          );
          if (selectedEmail) {
            selectedEmail.collapsed = true;
          }
        } else if (
          event.key === ' ' &&
          !event.target.isContentEditable &&
          event.target.tagName !== 'INPUT' &&
          event.target.tagName !== 'TEXTAREA'
        ) {
          event.preventDefault();
          event.stopPropagation();

          // If no email is selected, select the first one
          if (!this.selectedEmailId) {
            const allCollapsed = this.recentFilteredEmails.every(
              (email) => email.collapsed,
            );
            if (allCollapsed && this.recentEmails.length > 0) {
              const firstEmail = this.recentEmails[0];
              this.selectedEmailId = firstEmail.id;
              firstEmail.collapsed = false;

              this.$nextTick(() => {
                const emailElement = Array.from(
                  document.querySelectorAll('.email-card'),
                ).find((el) => el.classList.contains('selected-email'));

                if (emailElement) {
                  const innerWrapper = emailElement.closest('.inner-wrapper');
                  if (innerWrapper) {
                    innerWrapper.scrollTo({
                      top: 0,
                      behavior: 'smooth',
                    });
                  }
                }
              });
              return;
            }
          }

          // Existing functionality when an email is already selected
          const currentIndex = this.recentFilteredEmails.findIndex(
            (em) => em.id === this.selectedEmailId,
          );
          if (currentIndex !== -1) {
            const currentEmail = this.recentFilteredEmails[currentIndex];
            if (!currentEmail.collapsed) {
              // Mark as read via API and wait for it to complete
              await this.markEmailAsRead(currentEmail.id);

              // Find the original email in recentEmails array by index for direct modification
              const originalEmailIndex = this.recentEmails.findIndex(
                (e) => e.id === currentEmail.id,
              );
              if (originalEmailIndex !== -1) {
                // Use $set or direct assignment with spread to ensure reactivity
                this.recentEmails[originalEmailIndex] = {
                  ...this.recentEmails[originalEmailIndex],
                  collapsed: true,
                  read: true,
                };
              }

              const nextIndex = currentIndex + 1;
              if (nextIndex < this.recentFilteredEmails.length) {
                const nextEmail = this.recentFilteredEmails[nextIndex];
                this.selectedEmailId = nextEmail.id;

                // Find and update the next email in recentEmails array
                const originalNextEmailIndex = this.recentEmails.findIndex(
                  (e) => e.id === nextEmail.id,
                );
                if (originalNextEmailIndex !== -1) {
                  this.recentEmails[originalNextEmailIndex] = {
                    ...this.recentEmails[originalNextEmailIndex],
                    collapsed: false,
                  };
                }

                this.$nextTick(() => {
                  // Find the email card by matching the id with the selected email id
                  const nextEmailElement = Array.from(
                    document.querySelectorAll('.email-card'),
                  ).find((el) => el.classList.contains('selected-email'));

                  if (nextEmailElement) {
                    // Get the parent inner-wrapper that has the scrolling capability
                    const innerWrapper =
                      nextEmailElement.closest('.inner-wrapper');
                    if (innerWrapper) {
                      // Calculate the position to scroll to
                      const wrapperRect = innerWrapper.getBoundingClientRect();
                      const elementRect =
                        nextEmailElement.getBoundingClientRect();
                      const scrollTop =
                        innerWrapper.scrollTop +
                        (elementRect.top - wrapperRect.top);

                      // Scroll to the element
                      innerWrapper.scrollTo({
                        top: scrollTop,
                        behavior: 'smooth',
                      });
                    }
                  }
                });
              } else {
                this.selectedEmailId = null;
              }
            }
          }
        }
      }
    },
    filterUnread() {
      this.hideRead = !this.hideRead;
    },
    async initDB() {
      if (this.emailDb && typeof this.emailDb.transaction === 'function') {
        return this.emailDb;
      }
      if (this.emailDbPromise) return this.emailDbPromise;
      if (!window.indexedDB) {
        console.error(
          'Your browser doesnt support a stable version of IndexedDB.',
        );
        return null;
      }

      this.emailDbPromise = openDB('emailDB', 1, {
        upgrade(db) {
          if (!db.objectStoreNames.contains('emails')) {
            db.createObjectStore('emails', { keyPath: 'id' });
          }
        },
      })
        .then((db) => {
          this.emailDb = db;
          return db;
        })
        .catch((error) => {
          this.emailDbPromise = null;
          console.error('Error opening email database:', error);
          return null;
        });
      return this.emailDbPromise;
    },

    // Handle storage events from other tabs
    setupStorageListener() {
      window.addEventListener('storage', (event) => {
        if (event.key === 'emailReadStatus') {
          try {
            const { emailId, read } = JSON.parse(event.newValue);
            // Find the email in our list and update its read status
            const emailToUpdate = this.recentEmails.find(
              (email) => email.id === emailId,
            );
            if (emailToUpdate) {
              emailToUpdate.read = read;
            }
          } catch (error) {
            console.error('Error processing emailReadStatus event:', error);
          }
        }
      });
    },

    async markEmailAsRead(emailId, readState = true) {
      const db = await this.initDB();
      if (!db || typeof db.transaction !== 'function') return;
      const tx = db.transaction('emails', 'readwrite');
      const store = tx.objectStore('emails');
      await store.put({ id: emailId, read: readState });
      await tx.done;

      // Broadcast the change to other tabs
      localStorage.setItem(
        'emailReadStatus',
        JSON.stringify({
          emailId,
          read: readState,
          timestamp: Date.now(),
        }),
      );
    },
    async isEmailRead(emailId) {
      const db = await this.initDB();
      if (!db || typeof db.transaction !== 'function') return false;
      const tx = db.transaction('emails', 'readonly');
      const store = tx.objectStore('emails');
      const email = await store.get(emailId);
      await tx.done;
      return email ? email.read : false;
    },
    async selectEmailCard(id) {
      this.selectedEmailId = id;
    },

    async toggleEmailCollapse(emailId, event) {
      // Don't collapse when interacting with body/reply/attachments/forward
      if (
        event.target.closest('.email-body-content') ||
        event.target.closest('.reply-content-container') ||
        event.target.closest('.email-attachment-icon') ||
        event.target.closest('.email-footer-actions') ||
        event.target.closest('.email-footer-forward-btn')
      ) {
        event.stopPropagation();
        return;
      }
      const email = this.recentEmails.find((email) => email.id === emailId);
      if (email) {
        const wasCollapsed = email.collapsed;
        const wasExpanded = !email.collapsed;

        // Toggle collapse state
        email.collapsed = !email.collapsed;

        // Mark as read when collapsing an expanded email (user has seen it)
        if (wasExpanded && email.collapsed && !email.read) {
          email.read = true;
          await this.markEmailAsRead(emailId, true);
        }

        // If we just expanded the email, track the view and fetch latest data
        if (wasCollapsed && !email.collapsed) {
          await this.trackEmailView(email);
        }
      }
    },

    async trackEmailView(email) {
      try {
        const response = await axios.post('/api/email/track-view', {
          emailId: email.id,
        });

        if (response.data) {
          // Update local email data with server response
          email.readBy = response.data.readBy || '';
          email.repliedOrSentBy = response.data.repliedOrSentBy || '';

          // If there's a replyId that we didn't have before, update it
          if (response.data.replyId && !email.replyId) {
            email.replyId = response.data.replyId;
          }

          // Update notes in case it was added by someone else
          if (response.data.notes !== undefined) {
            email.notes = response.data.notes;
          }
        }
      } catch (error) {
        console.error('Error tracking email view:', error);
      }
    },

    async toggleReplyContent(email) {
      // Find the email in recentEmails array to ensure we're working with the reactive object
      const emailIndex = this.recentEmails.findIndex((e) => e.id === email.id);
      if (emailIndex === -1) return;

      const targetEmail = this.recentEmails[emailIndex];

      // Toggle the reply content visibility
      targetEmail.showReplyContent = !targetEmail.showReplyContent;

      // Force reactivity update by replacing the object in the array
      this.recentEmails.splice(emailIndex, 1, { ...targetEmail });

      // Re-fetch the reference after splice
      const updatedEmail = this.recentEmails[emailIndex];

      // If showing and we don't have the reply email content yet, fetch it
      if (
        updatedEmail.showReplyContent &&
        updatedEmail.replyId &&
        !updatedEmail.replyEmail
      ) {
        await this.fetchReplyEmail(updatedEmail, emailIndex);
      }
    },

    /** Green "ET replied" footer text: expand card and open the reply panel. */
    async openReplyFromIndicator(email, event) {
      if (event) {
        event.stopPropagation();
        event.preventDefault();
      }

      const emailIndex = this.recentEmails.findIndex((e) => e.id === email.id);
      if (emailIndex === -1) return;

      const targetEmail = this.recentEmails[emailIndex];

      if (targetEmail.collapsed) {
        targetEmail.collapsed = false;
        await this.trackEmailView(targetEmail);
      }

      this.selectedEmailId = targetEmail.id;
      targetEmail.showReplyContent = true;
      this.recentEmails.splice(emailIndex, 1, { ...targetEmail });

      const updatedEmail = this.recentEmails[emailIndex];
      if (
        updatedEmail.replyId &&
        !updatedEmail.replyEmail
      ) {
        await this.fetchReplyEmail(updatedEmail, emailIndex);
      }
    },

    async fetchReplyEmail(email, emailIndex) {
      try {
        const response = await axios.get(`/api/email/${email.replyId}`);
        if (response.data) {
          // Update the email with reply data and force reactivity
          email.replyEmail = response.data;
          this.recentEmails.splice(emailIndex, 1, { ...email });
        }
      } catch (error) {
        console.error('Error fetching reply email:', error);
      }
    },

    openLink() {
      if (this.docusignUrl) {
        window.open(this.docusignUrl, '_blank');
        this.showUrlConfirm = false;
      }
    },
    closeUrlConfirm(event) {
      console.log('close');
      event.preventDefault();
      this.showUrlConfirm = false;
    },
    toggleClipTool() {
      this.showClipTool = !this.showClipTool;
    },
    toggleAll() {
      this.ledgerList.forEach((item) => {
        item.attach = !item.attach;
      });
    },
    highlightMatch(content) {
      if (typeof content !== 'string' || !this.emailFilter) return content;

      const query = this.emailFilter.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`(${query})`, 'gi');
      return content.replace(regex, '<mark>$1</mark>');
    },

    // Helper method to decode quoted-printable encoding
    decodeQuotedPrintable(text) {
      if (!text || typeof text !== 'string') return '';

      // First handle line breaks with = at the end (soft line breaks)
      text = text.replace(/=\r?\n/g, '');

      // Then decode hex-encoded characters
      return text.replace(/=([0-9A-F]{2})/gi, (match, hex) => {
        try {
          return String.fromCharCode(parseInt(hex, 16));
        } catch (e) {
          return match;
        }
      });
    },

    // Helper to decode UTF-8 sequences that might be present after quoted-printable decoding
    decodeUtf8Sequences(text) {
      try {
        // Convert potential UTF-8 byte sequences to proper characters
        const bytes = [];
        for (let i = 0; i < text.length; i++) {
          bytes.push(text.charCodeAt(i));
        }
        return new TextDecoder('utf-8').decode(new Uint8Array(bytes));
      } catch (e) {
        return text;
      }
    },

    // Helper function to filter out non-printable characters
    filterPrintableChars(text) {
      if (!text || typeof text !== 'string') return text;

      // Keep only printable ASCII characters (32-126), plus common whitespace and HTML entities
      // This will remove mojibake characters while preserving HTML structure
      return text.replace(/[^\x20-\x7E\r\n\t&;]/g, '');
    },

    // Helper function to strip style attributes and style tags
    stripStyles(html) {
      if (!html || typeof html !== 'string') return html;

      // Remove <style> tags and their contents
      let cleaned = html.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');

      // Remove inline style attributes
      cleaned = cleaned.replace(/\s+style\s*=\s*["'][^"']*["']/gi, '');

      // Remove color attributes (deprecated but sometimes used)
      cleaned = cleaned.replace(/\s+color\s*=\s*["'][^"']*["']/gi, '');

      // Remove font tags (deprecated but sometimes used)
      cleaned = cleaned.replace(/<font[^>]*>/gi, '');
      cleaned = cleaned.replace(/<\/font>/gi, '');

      return cleaned;
    },

    dropDuplicatePlaintextBeforeHtml(body) {
      const htmlStart = body.search(/<(?:html|body|table|div|p|span)\b/i);
      if (htmlStart <= 0) return body;
      const prefix = body.slice(0, htmlStart);
      const html = body.slice(htmlStart);
      if (/<(?:html|body|table|div|p|span|br|tr|td)\b/i.test(prefix)) return body;
      const prefixText = prefix.replace(/\s+/g, ' ').trim();
      if (prefixText.length < 40) return body;
      const htmlText = html
        .replace(/=\r?\n/g, '')
        .replace(/=([A-Fa-f0-9]{2})/g, (_, hex) =>
          String.fromCharCode(parseInt(hex, 16)),
        )
        .replace(/<[^>]+>/g, ' ')
        .replace(/&nbsp;/gi, ' ')
        .replace(/\s+/g, ' ');
      const needle = prefixText.slice(0, Math.min(40, prefixText.length));
      if (htmlText.includes(needle)) return html.trim();
      return body;
    },

    recentBody(body, emailId) {
      if (!body || typeof body !== 'string') return '';

      body = this.dropDuplicatePlaintextBeforeHtml(body);

      // Check if content is already HTML (contains HTML tags)
      const isHtml = /<[^>]+>/.test(body);

      if (isHtml) {
        // Content is already sanitized HTML from the backend
        // Filter out non-printable characters that cause display issues
        let filteredBody = this.filterPrintableChars(body);
        // Strip style information
        filteredBody = this.stripStyles(filteredBody);
        if (emailId) {
          const cids = referencedCids(filteredBody);
          this.queueInlineFileKeys(emailId, cids);
          const keyMap = {};
          for (const cid of cids) {
            const key = this.inlineFileKeys[`${emailId}:${cid}`];
            if (key) keyMap[cid] = key;
          }
          filteredBody = rewriteLoggedCidImages(filteredBody, emailId, keyMap);
        }
        return filteredBody;
      } else {
        // Fallback to original processing for non-HTML content
        // Step 1: Decode quoted-printable content
        const decodedBody = this.decodeQuotedPrintable(body);

        // Step 2: Try to decode any UTF-8 sequences that might be present
        const utf8Decoded = this.decodeUtf8Sequences(decodedBody);

        // Step 3: Sanitize the content to remove any remaining problematic characters
        const sanitizedBody = utf8Decoded.replace(
          /[^\p{L}\p{N}.,?!;:'"(){}[\]<>%@#$&*\-=_+~`|\\/\s]/gu,
          '',
        );

        // Step 4: Parse the markdown
        return marked.parse(sanitizedBody);
      }
    },

    recentBodyPlaintext(body) {
      if (!body || typeof body !== 'string') return '';

      // Step 1: Decode quoted-printable content
      const decodedBody = this.decodeQuotedPrintable(body);

      // Step 2: Try to decode any UTF-8 sequences that might be present
      const utf8Decoded = this.decodeUtf8Sequences(decodedBody);

      // Step 3: Remove HTML tags, replace non-breaking spaces, and remove non-printable characters
      const strippedBody = utf8Decoded
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/[^\x20-\x7E\r\n]/g, '');

      // Step 4: Format text with line breaks for readability
      const wrappedBody = strippedBody
        .replace(/(.{1,70})(\s+|$)/g, '$1\n')
        .trim();

      return wrappedBody;
    },

    handleEmailCardClick(email, event) {
      console.log('handleEmailCardClick', email, event.target.classList);
      if (email.from?.includes('@docusign.net')) {
        const urlPattern =
          /https:\/\/na4\.docusign\.net\/(?:Signing\/EmailStart\.aspx\?[^"'\s]+|signing\/emails\/v1-[0-9a-f]+)/gi;

        const strippedBody = email.body;
        const matches = strippedBody.match(urlPattern);

        if (matches && matches.length > 0) {
          this.docusignUrl = matches[0];
          this.showUrlConfirm = true;
          this.$nextTick(() => {
            const showUrlConfirmElement = this.$refs.showUrlConfirm;
            if (showUrlConfirmElement && event) {
              showUrlConfirmElement.style.position = 'absolute';
              showUrlConfirmElement.style.left = `${event.pageX}px`;
              showUrlConfirmElement.style.top = `${event.pageY}px`;
            }
            return;
          });

          if (navigator.clipboard) {
            navigator.clipboard
              .writeText(this.docusignUrl)
              .then(() => {
                console.log('URL copied to clipboard:', this.docusignUrl);
              })
              .catch((err) => {
                console.error('Failed to copy URL: ', err);
              });
          } else {
            console.log('URL not copied to clipboard:', this.docusignUrl);
          }
        }
      } else if (event.target.closest('email-delete')) {
        console.log('delete detected');
        event.stopPropagation();
      } else {
        this.openComposePopup(email);
      }
    },
    async fetchRecentCommunications() {
      this.isLoading = true;
      const emails = recentEmailFetchAddresses(this.emailFilter);
      const includeDeleted = this.includeDeleted;

      try {
        const response = await axios.post('/api/recent-emails', {
          emails,
          includeDeleted,
          limit: this.emailLimit,
        });

        const fetchedEmails = await Promise.all(
          response.data.map(async (email) => ({
            ...email,
            read: await this.isEmailRead(email.id),
            collapsed: true,
          })),
        );

        this.recentEmails = fetchedEmails;

        // Fetch property addresses for all unique 'from' emails
        this.fetchEmailAddressMap(fetchedEmails);

        this.acknowledgeFetchedEmails(fetchedEmails);
      } catch (error) {
        console.error('Error fetching recent emails:', error);
      } finally {
        setQuickEmailLastFetchAt(this.userId);
        this.isLoading = false;
        this.$nextTick(() => {
          if (this.pendingBriefEmail) {
            this.applyPendingBriefEmail();
            return;
          }
          if (this.showEmailReview) {
            this.reconcileEmailReviewIndex();
            this.activateEmailReviewCurrent();
          } else {
            this.maybeOpenPinnedEmailReview();
          }
        });
      }
    },
    async fetchEmailAddressMap(emails) {
      try {
        const uniqueFromEmails = [
          ...new Set(
            emails.map((e) => e.from).filter((f) => f && f.trim() !== ''),
          ),
        ];
        if (uniqueFromEmails.length === 0) return;
        const response = await axios.post('/api/email-to-address', {
          emails: uniqueFromEmails,
        });
        this.emailAddressMap = {
          ...this.emailAddressMap,
          ...(response.data || {}),
        };
      } catch (error) {
        console.error('Error fetching email address map:', error);
      }
    },
    getPropertyAddress(email) {
      if (!email) return '';
      // Strip any HTML markup (from highlight) before lookup
      const raw = email.replace(/<[^>]+>/g, '').trim();
      const addr = this.emailAddressMap[raw.toLowerCase()];
      return addr ? ` (${addr})` : '';
    },
    async fetchEmailById(emailId) {
      try {
        const response = await axios.get(`/api/email/${emailId}`);
        const email = response.data;
        email.read = await this.isEmailRead(email.id);
        email.collapsed = true;
        this.recentEmails.unshift(email);
        // Fetch address for the new email's from address if not already known
        if (email.from && !this.emailAddressMap[email.from.toLowerCase()]) {
          this.fetchEmailAddressMap([email]);
        }
        eventBus.emit('emailsUnseenCountChanged');
      } catch (error) {
        console.error('Error fetching email by ID:', error);
      }
    },
    formatDate(dateString, ago = false) {
      const date = new Date(dateString);

      const now = new Date();
      const diffInMs = now - date;

      // Ensure we don't show negative values (can happen with timezone/clock issues)
      const diffInHours = Math.max(0, Math.floor(diffInMs / (1000 * 60 * 60)));
      const diffInDays = Math.floor(diffInHours / 24);

      let formattedDate = date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });

      if (ago) {
        if (diffInHours < 24) {
          formattedDate += ` (${diffInHours} hr${diffInHours !== 1 ? 's' : ''} ago)`;
        } else {
          formattedDate += ` (${diffInDays} day${diffInDays !== 1 ? 's' : ''} ago)`;
        }
      }

      return formattedDate;
    },
    emailHasAttachments(email) {
      const body = (email && email.body) || '';
      return this.getAttachmentLinksFromBody(body).length > 0;
    },

    /**
     * Decode RFC 2047 encoded-words and the mangled form stored for some
     * inbound attachments: =_utf-8_B_<base64>_=.pdf
     */
    decodeMimeWordFilename(name) {
      if (!name || typeof name !== 'string') return name || '';

      let decoded = name;

      // Proper =?charset?B?data?= / =?charset?Q?data?=
      decoded = decoded.replace(
        /=\?([^?]+)\?([bBqQ])\?([^?]*)\?=/g,
        (full, _charset, enc, data) => {
          try {
            if (enc.toUpperCase() === 'B') {
              return atob(data.replace(/\s/g, ''));
            }
            // Q-encoding: _ = space, =XX hex
            return data
              .replace(/_/g, ' ')
              .replace(/=([0-9A-Fa-f]{2})/g, (_, hex) =>
                String.fromCharCode(parseInt(hex, 16)),
              );
          } catch {
            return full;
          }
        },
      );

      // Mangled sanitize form: =_utf-8_B_<base64>_=  or  =_utf-8_B_<base64>_=.pdf
      decoded = decoded.replace(
        /=_([^_]+)_([bB])_([A-Za-z0-9+/=]+)_=(.*)$/g,
        (full, _charset, enc, data, rest) => {
          try {
            if (enc.toUpperCase() === 'B') {
              const plain = atob(data.replace(/\s/g, ''));
              // rest is often ".pdf" when ext was appended after encoding
              if (rest && !plain.toLowerCase().endsWith(rest.toLowerCase())) {
                return plain + rest;
              }
              return plain || full;
            }
          } catch {
            /* fall through */
          }
          return full;
        },
      );

      return decoded;
    },

    fullyDecodeURIComponent(value) {
      if (value == null) return '';
      let prev = String(value);
      for (let i = 0; i < 5; i++) {
        try {
          const next = decodeURIComponent(prev);
          if (next === prev) break;
          prev = next;
        } catch {
          break;
        }
      }
      return prev;
    },

    normalizeAttachmentLink(attachment) {
      if (!attachment || !attachment.url) return null;

      // Undo accidental double-encoding in stored metadata URLs
      let rawUrl = this.fullyDecodeURIComponent(attachment.url);
      // Rebuild a clean relative or absolute path (encode once on request)
      let pathPart = rawUrl;
      let queryName = '';
      try {
        const base = window.location.origin;
        const u = new URL(rawUrl, base);
        pathPart = u.pathname;
        queryName = u.searchParams.get('name') || '';
      } catch {
        const [p, q] = String(rawUrl).split('?');
        pathPart = p;
        if (q) {
          const m = q.match(/(?:^|&)name=([^&]*)/);
          if (m) queryName = m[1];
        }
      }
      pathPart = this.fullyDecodeURIComponent(pathPart);
      queryName = this.fullyDecodeURIComponent(queryName);

      const storedBase = pathPart.split('/').pop() || 'attachment';
      let fileName = this.decodeMimeWordFilename(
        attachment.fileName || queryName || storedBase,
      );
      fileName = this.fullyDecodeURIComponent(fileName);
      fileName = this.decodeMimeWordFilename(fileName);

      if (queryName) {
        const decodedQ = this.decodeMimeWordFilename(
          this.fullyDecodeURIComponent(queryName),
        );
        if (
          decodedQ &&
          !/^=_utf-8_/i.test(decodedQ) &&
          !/^=\?/.test(decodedQ)
        ) {
          fileName = decodedQ;
        }
      }

      if (!/\.[a-z0-9]{2,5}$/i.test(fileName)) {
        const extMatch = storedBase.match(/(\.[a-z0-9]{2,5})$/i);
        if (extMatch) fileName += extMatch[1];
      }

      // Relative API path; axios will encode the path safely once
      const cleanUrl = queryName
        ? `${pathPart}?name=${encodeURIComponent(fileName)}`
        : pathPart;

      return {
        url: cleanUrl,
        fileName: fileName || 'attachment',
      };
    },

    getAttachmentMetadataFromBody(body) {
      if (!body || typeof body !== 'string') return [];

      const metadataMatch = body.match(
        /<!--\s*email-attachments:([\s\S]*?)-->/i,
      );
      if (!metadataMatch || !metadataMatch[1]) return [];

      try {
        const decodedMetadata = decodeURIComponent(metadataMatch[1].trim());
        const parsedMetadata = JSON.parse(decodedMetadata);
        if (!Array.isArray(parsedMetadata)) return [];
        return parsedMetadata
          .map((item) => this.normalizeAttachmentLink(item))
          .filter(Boolean);
      } catch (error) {
        return [];
      }
    },
    getAttachmentLinksFromBody(body) {
      if (!body || typeof body !== 'string') return [];

      const metadataLinks = this.getAttachmentMetadataFromBody(body);
      const parser = new DOMParser();
      const doc = parser.parseFromString(body, 'text/html');
      const links = Array.from(doc.querySelectorAll('a[href]'));
      const seen = new Set();

      metadataLinks.forEach((attachment) => {
        if (attachment && attachment.url) {
          seen.add(attachment.url);
        }
      });

      return [
        ...metadataLinks,
        ...links
          .map((a) => {
            const href = a.getAttribute('href') || '';
            const text = (a.textContent || '').trim();
            const lowerHref = href.toLowerCase();
            const lowerText = text.toLowerCase();
            const isAttachmentLink =
              /\.(pdf|docx?|xlsx?|csv|txt|jpg|jpeg|png|gif|zip)(\?|#|$)/i.test(
                lowerHref,
              ) ||
              lowerHref.includes('/api/email-attachments/') ||
              lowerHref.includes('download') ||
              lowerHref.includes('attachment') ||
              lowerText.includes('attachment') ||
              lowerText.includes('download');

            if (!href || !isAttachmentLink) return null;

            let absoluteUrl = href;
            try {
              absoluteUrl = new URL(href, window.location.origin).toString();
            } catch (error) {
              return null;
            }

            if (seen.has(absoluteUrl)) return null;
            seen.add(absoluteUrl);

            const derivedName =
              text ||
              decodeURIComponent(absoluteUrl.split('/').pop() || 'attachment');

            return this.normalizeAttachmentLink({
              url: absoluteUrl,
              fileName: derivedName,
            });
          })
          .filter(Boolean),
      ];
    },
    isViewableAttachment(fileName, contentType = '') {
      return (
        this.isImageAttachment(fileName, contentType) ||
        this.isPdfAttachment(fileName, contentType)
      );
    },

    isPdfAttachment(fileName, contentType = '') {
      const name = this.decodeMimeWordFilename(fileName || '').toLowerCase();
      const type = (contentType || '').toLowerCase();
      return (
        type.includes('pdf') ||
        /\.pdf$/i.test(name) ||
        // MIME-mangled names often end with _=.pdf
        /_=\.pdf$/i.test(name) ||
        /pdf$/i.test(name)
      );
    },

    isImageAttachment(fileName, contentType = '') {
      const name = this.decodeMimeWordFilename(fileName || '').toLowerCase();
      const type = (contentType || '').toLowerCase();
      return (
        type.startsWith('image/') ||
        /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(name)
      );
    },

    revokeAttachmentViewerUrl() {
      if (this.pdfUrl) {
        URL.revokeObjectURL(this.pdfUrl);
        this.pdfUrl = '';
      }
    },

    setAttachmentViewerContent(blob, fileName) {
      this.revokeAttachmentViewerUrl();
      this.pdfUrl = URL.createObjectURL(blob);
      this.viewerTitle = fileName || 'Attachment';
      this.viewerIsImage = this.isImageAttachment(fileName, blob.type);
      clearTimeout(this.attachmentLoadTimer);
      this.attachmentLoadTimer = setTimeout(() => {
        this.attachmentViewerLoading = false;
      }, 8000);
    },

    openAttachmentViewerShell({ items, index = 0, title } = {}) {
      if (!this.showPDF) {
        document.addEventListener('keydown', this.onAttachmentViewerKeydown);
      }
      if (items) {
        this.attachmentViewerItems = items;
        this.attachmentViewerIndex = index;
        const item = items[index];
        this.viewerTitle = title || item?.fileName || 'Attachment';
        this.viewerIsImage = item
          ? this.isImageAttachment(item.fileName)
          : false;
      } else if (title) {
        this.viewerTitle = title;
      }
      this.attachmentViewerLoading = true;
      this.showPDF = true;
      this.$nextTick(() => this.resetAttachmentViewerPosition());
    },

    onAttachmentContentLoad() {
      clearTimeout(this.attachmentLoadTimer);
      this.attachmentViewerLoading = false;
    },

    openAttachmentInViewer(blob, fileName) {
      this.setAttachmentViewerContent(blob, fileName);

      if (!this.showPDF) {
        document.addEventListener('keydown', this.onAttachmentViewerKeydown);
      }

      this.showPDF = true;
      this.$nextTick(() => this.resetAttachmentViewerPosition());
    },

    closeAttachmentViewer() {
      this.stopAttachmentViewerDrag();
      document.removeEventListener('keydown', this.onAttachmentViewerKeydown);
      this.showPDF = false;
      this.attachmentViewerLoading = false;
      clearTimeout(this.attachmentLoadTimer);
      this.revokeAttachmentViewerUrl();
      this.attachmentViewerItems = [];
      this.attachmentViewerIndex = 0;
      this.viewerIsImage = false;
    },

    onAttachmentViewerKeydown(event) {
      if (!this.showPDF) {
        return;
      }

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.showPreviousAttachment();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.showNextAttachment();
      } else if (event.key === 'Escape') {
        event.preventDefault();
        this.closeAttachmentViewer();
      }
    },

    async showAttachmentAtIndex(index) {
      if (
        !this.attachmentViewerItems.length ||
        index < 0 ||
        index >= this.attachmentViewerItems.length
      ) {
        return false;
      }

      const attachment = this.attachmentViewerItems[index];
      this.attachmentViewerLoading = true;
      this.revokeAttachmentViewerUrl();
      this.viewerTitle = attachment.fileName || 'Attachment';

      try {
        const blob = await this.fetchAttachmentBlob(attachment.url);
        this.attachmentViewerIndex = index;

        if (this.showPDF) {
          this.setAttachmentViewerContent(blob, attachment.fileName);
        } else {
          this.openAttachmentInViewer(blob, attachment.fileName);
        }

        return true;
      } catch (error) {
        this.attachmentViewerLoading = false;
        const status = error.response?.status;

        if (status === 404) {
          this.updateFooterMessage('Attachment file not found on the server');
        } else {
          this.updateFooterMessage('Failed to load attachment');
        }

        return false;
      }
    },

    showPreviousAttachment() {
      if (this.attachmentViewerIndex > 0) {
        this.showAttachmentAtIndex(this.attachmentViewerIndex - 1);
      }
    },

    showNextAttachment() {
      if (this.attachmentViewerIndex < this.attachmentViewerItems.length - 1) {
        this.showAttachmentAtIndex(this.attachmentViewerIndex + 1);
      }
    },

    resetAttachmentViewerPosition() {
      const el = this.$refs.pdfViewer;
      if (!el) {
        return;
      }
      el.style.left = '';
      el.style.top = '';
      el.style.transform = '';
    },

    stopAttachmentViewerDrag() {
      document.removeEventListener('mousemove', this.onAttachmentViewerDrag);
      document.removeEventListener('mouseup', this.stopAttachmentViewerDrag);
      this.attachmentViewerDragState = null;
    },

    startAttachmentViewerDrag(event) {
      if (
        event.button !== 0 ||
        event.target.closest('.attachment-viewer-close-button')
      ) {
        return;
      }

      const el = this.$refs.pdfViewer;
      if (!el) {
        return;
      }

      event.preventDefault();

      const rect = el.getBoundingClientRect();
      el.style.transform = 'none';
      el.style.left = `${rect.left}px`;
      el.style.top = `${rect.top}px`;

      this.attachmentViewerDragState = {
        startX: event.clientX,
        startY: event.clientY,
        startLeft: rect.left,
        startTop: rect.top,
      };

      document.addEventListener('mousemove', this.onAttachmentViewerDrag);
      document.addEventListener('mouseup', this.stopAttachmentViewerDrag);
    },

    onAttachmentViewerDrag(event) {
      const el = this.$refs.pdfViewer;
      const drag = this.attachmentViewerDragState;
      if (!el || !drag) {
        return;
      }

      event.preventDefault();
      el.style.left = `${drag.startLeft + (event.clientX - drag.startX)}px`;
      el.style.top = `${drag.startTop + (event.clientY - drag.startY)}px`;
    },

    triggerAttachmentDownload(blob, fileName) {
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName || 'attachment';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
    },

    resolveAttachmentUrl(url) {
      if (!url) return url;

      // Fully decode then re-parse so path is not double-encoded for axios
      const normalized = this.normalizeAttachmentLink({
        url,
        fileName: '',
      });
      const clean = (normalized && normalized.url) || url;

      if (/^https?:\/\//i.test(clean)) {
        return clean;
      }

      const base = axios.defaults.baseURL || window.location.origin;
      // Encode each path segment once (handles = and spaces in stored names)
      try {
        const u = new URL(clean, base);
        const segments = u.pathname.split('/').map((seg, i) => {
          if (i === 0 && seg === '') return '';
          try {
            return encodeURIComponent(decodeURIComponent(seg));
          } catch {
            return encodeURIComponent(seg);
          }
        });
        u.pathname = segments.join('/');
        return u.toString();
      } catch {
        return new URL(clean, base).toString();
      }
    },

    async getAuthenticatedAttachmentUrl(url) {
      const resolvedUrl = this.resolveAttachmentUrl(url);
      const authedUrl = new URL(resolvedUrl, window.location.origin);
      const key = await this.requestFileAccessKey(authedUrl.pathname);
      authedUrl.searchParams.set('fileKey', key);
      return authedUrl.toString();
    },

    async fetchAttachmentBlob(url) {
      const resolved = this.resolveAttachmentUrl(url);
      const response = await axios.get(resolved, {
        responseType: 'blob',
        // Avoid axios treating already-built absolute URLs oddly
        validateStatus: (status) => status >= 200 && status < 300,
      });
      return response.data;
    },

    async handleAttachment(attachment, { openInViewer = false } = {}) {
      const fileName = attachment.fileName || 'attachment';

      try {
        const blob = await this.fetchAttachmentBlob(attachment.url);

        if (openInViewer && this.isViewableAttachment(fileName, blob.type)) {
          this.attachmentViewerItems = [attachment];
          this.attachmentViewerIndex = 0;
          this.attachmentViewerLoading = true;
          this.openAttachmentInViewer(blob, fileName);
          return 'opened';
        }

        this.triggerAttachmentDownload(blob, fileName);
        return 'downloaded';
      } catch (error) {
        const status = error.response?.status;

        if (status === 404) {
          this.updateFooterMessage(
            'Attachment file not found on the server',
          );
          return 'missing';
        }

        if (openInViewer && this.isViewableAttachment(fileName)) {
          try {
            const openUrl = await this.getAuthenticatedAttachmentUrl(
              attachment.url,
            );
            window.open(openUrl, '_blank');
            return 'opened-tab';
          } catch (openError) {
            this.updateFooterMessage('Failed to open attachment');
            return 'failed';
          }
        }

        this.updateFooterMessage('Failed to open attachment');
        return 'failed';
      }
    },

    async downloadAllAttachments(email) {
      const links = this.getAttachmentLinksFromBody(
        (email && email.body) || '',
      );

      if (links.length === 0) {
        this.updateFooterMessage(
          'No downloadable attachments found for this email',
        );
        return;
      }

      this.updateFooterMessage(
        links.length === 1
          ? 'Opening attachment…'
          : `Opening ${links.length} attachments…`,
      );

      const viewableLinks = links.filter((link) =>
        this.isViewableAttachment(link.fileName),
      );
      // If extension detection fails, still try as viewable (PDF is most common)
      const nonViewableLinks = links.filter(
        (link) => !this.isViewableAttachment(link.fileName),
      );
      const toOpen =
        viewableLinks.length > 0 ? viewableLinks : nonViewableLinks;
      const toDownload =
        viewableLinks.length > 0 ? nonViewableLinks : [];

      let openedCount = 0;
      let downloadedCount = 0;
      let failedCount = 0;

      if (toOpen.length > 0) {
        this.openAttachmentViewerShell({ items: toOpen, index: 0 });
        if (await this.showAttachmentAtIndex(0)) {
          openedCount = 1;
        } else {
          // Viewer failed — fall back to download for each
          for (const attachment of toOpen) {
            const result = await this.handleAttachment(attachment, {
              openInViewer: false,
            });
            if (result === 'downloaded') downloadedCount += 1;
            else if (result === 'missing' || result === 'failed') {
              failedCount += 1;
            }
          }
        }
      }

      for (const attachment of toDownload) {
        const result = await this.handleAttachment(attachment, {
          openInViewer: false,
        });

        if (result === 'downloaded') {
          downloadedCount += 1;
        } else if (result === 'missing' || result === 'failed') {
          failedCount += 1;
        }
      }

      if (openedCount > 0 && downloadedCount > 0) {
        this.updateFooterMessage(
          `Opened attachment viewer and downloaded ${downloadedCount} more`,
        );
      } else if (openedCount > 0) {
        const browseHint =
          toOpen.length > 1 ? ' — use arrows to browse attachments' : '';
        this.updateFooterMessage(`Opened attachment in viewer${browseHint}`);
      } else if (downloadedCount > 0) {
        this.updateFooterMessage(
          `Started ${downloadedCount} attachment download${downloadedCount === 1 ? '' : 's'}`,
        );
      } else if (failedCount > 0) {
        this.updateFooterMessage(
          'Attachment file not found on the server (may have been cleaned up)',
        );
      } else {
        this.updateFooterMessage('Could not open or download attachments');
      }
    },
    increaseEmailLimit() {
      this.emailLimit += 500;
      this.fetchRecentCommunications();
    },
    updatePreview() {
      const combinedBody = this.quickEmailBody + this.quickEmailFooter;
      const parsedBody = marked.parse(combinedBody);

      const printableBody = parsedBody.replace(/[^\x20-\x7E]/g, '');

      this.markedBody = printableBody;
    },
    blobToDataUrl(blob) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ''));
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(blob);
      });
    },
    async compressInlineImage(blob) {
      const tooLarge = (file) => file && file.size > INLINE_UPLOAD_MAX_BYTES;
      try {
        const compressed = await imageCompression(blob, {
          maxSizeMB: 1.5,
          maxWidthOrHeight: 1600,
          initialQuality: 0.8,
          fileType: 'image/jpeg',
          useWebWorker: true,
        });
        const file = new File([compressed], 'screenshot.jpg', {
          type: 'image/jpeg',
        });
        if (tooLarge(file)) {
          this.updateFooterMessage('Pasted image is too large to send.');
          return null;
        }
        const dataUrl = await this.blobToDataUrl(file);
        return { blob: file, mime: 'image/jpeg', dataUrl };
      } catch (error) {
        console.error('Error compressing pasted image:', error);
        if (tooLarge(blob)) {
          this.updateFooterMessage('Pasted image is too large to send.');
          return null;
        }
        try {
          const dataUrl = await this.blobToDataUrl(blob);
          return {
            blob,
            mime: blob.type || 'image/png',
            dataUrl,
          };
        } catch {
          this.updateFooterMessage('Could not read the pasted image.');
          return null;
        }
      }
    },
    async handleBodyPaste(event) {
      const item = firstClipboardImageItem(
        event.clipboardData && event.clipboardData.items,
      );
      if (!item) return;

      event.preventDefault();

      if (!canAddInlineImage(referencedCids(this.quickEmailBody).length)) {
        this.updateFooterMessage(
          `At most ${MAX_INLINE_IMAGES} inline images per email.`,
        );
        return;
      }

      const file =
        typeof item.getAsFile === 'function' ? item.getAsFile() : null;
      if (!file || !file.size) {
        this.updateFooterMessage('Could not read the pasted image.');
        return;
      }

      const cid = `paste-${this.nextInlineId}`;
      this.nextInlineId += 1;

      const compressed = await this.compressInlineImage(file);
      if (!compressed) return;

      if (!canAddInlineImage(referencedCids(this.quickEmailBody).length)) {
        this.updateFooterMessage(
          `At most ${MAX_INLINE_IMAGES} inline images per email.`,
        );
        return;
      }
      const objectUrl = URL.createObjectURL(compressed.blob);
      this.inlineImages.push({
        cid,
        blob: compressed.blob,
        objectUrl,
        mime: compressed.mime,
        dataUrl: compressed.dataUrl,
      });

      const el = this.$refs.quickEmailBody;
      const start = el ? el.selectionStart : (this.quickEmailBody || '').length;
      const end = el ? el.selectionEnd : start;
      const inserted = insertAtCursor(
        this.quickEmailBody,
        start,
        end,
        markdownMarker(cid),
      );
      this.quickEmailBody = inserted.text;
      this.$nextTick(() => {
        if (this.$refs.quickEmailBody) {
          this.$refs.quickEmailBody.focus();
          this.$refs.quickEmailBody.selectionStart = inserted.cursor;
          this.$refs.quickEmailBody.selectionEnd = inserted.cursor;
        }
      });
      this.updatePreview();
      this.scheduleComposeDraftSave();
    },
    clearInlineImages() {
      for (const img of this.inlineImages || []) {
        if (img && img.objectUrl) {
          try {
            URL.revokeObjectURL(img.objectUrl);
          } catch {
            /* ignore */
          }
        }
      }
      this.inlineImages = [];
      this.nextInlineId = 1;
    },
    revokeUnusedInlineImages(kept) {
      const keepCids = new Set((kept || []).map((img) => img.cid));
      for (const img of this.inlineImages || []) {
        if (img && img.objectUrl && !keepCids.has(img.cid)) {
          try {
            URL.revokeObjectURL(img.objectUrl);
          } catch {
            /* ignore */
          }
        }
      }
    },
    appendInlineImagesToFormData(formData) {
      const needed = pruneInlineImages(this.inlineImages, this.quickEmailBody);
      formData.append(
        'inlineCids',
        JSON.stringify(needed.map((img) => img.cid)),
      );
      for (const img of needed) {
        const file = new File([img.blob], `${img.cid}.jpg`, {
          type: img.mime || 'image/jpeg',
        });
        formData.append('inlineImage', file);
      }
    },
    handleFileSelection(event) {
      this.selectedFiles = Array.from(event.target.files);
    },
    closeModal() {
      this.showQuickEmails = false;
      this.showComposePopup = false;
      this.quickEmailTo = '';
      this.quickEmailSubject = '';
      this.quickEmailBody = '';
      this.selectedFiles = [];
      this.useSelectedAttachments = false;
      this.disableSendEmail = false;
      this.clearInlineImages();
    },
    async getIdfromEmail(email) {
      try {
        const response = await axios.get('/api/get-id-from-email', {
          params: { email },
        });

        const id = response.data.id;

        this.logContact(id);
      } catch (error) {
        console.error(error);

        if (error.response && error.response.status === 404) {
          this.logContact(0);
        } else {
          this.logContact(0);
        }
      }
    },
    async logContact(personId, type = 1) {
      try {
        await axios.post('/api/log-contact', {
          type: type,
          personId: personId,
        });
      } catch (error) {
        console.error(error);
      }
    },
    async sendQuickEmail() {
      this.disableSendEmail = true;
      this.sendingEmail = true;

      if (
        !this.quickEmailTo ||
        !this.quickEmailSubject ||
        !this.quickEmailBody
      ) {
        this.footerMessage =
          'Missing To, Subject, or Body. Please correct before continuing.';
        this.disableSendEmail = false;
        this.sendingEmail = false;
        return;
      }

      const danglingCids = missingReferencedCids(
        this.quickEmailBody,
        this.inlineImages,
      );
      if (danglingCids.length) {
        this.updateFooterMessage(
          'A pasted screenshot is missing. Paste it again before sending.',
        );
        this.disableSendEmail = false;
        this.sendingEmail = false;
        return;
      }

      const filesToEmail = [];

      if (this.selectedFiles && this.selectedFiles.length > 0) {
        filesToEmail.push(...this.selectedFiles);
        console.log('selected files added');
      }

      if (this.useSelectedAttachments) {
        for (const item of this.ledgerList) {
          if (item.attach) {
            try {
              const response = await axios.get(
                `/api/fetch-ledgerlist-blob/${item.id}`,
                {
                  responseType: 'blob',
                },
              );

              const pdfBlob = new Blob([response.data], {
                type: 'application/pdf',
              });
              const pdfFile = new File([pdfBlob], `${item.docTitle}.pdf`, {
                type: 'application/pdf',
              });
              filesToEmail.push(pdfFile);
            } catch (error) {
              console.error(
                `Failed to fetch attachment for ${item.docTitle}`,
                error,
              );
              this.footerMessage = `Failed to fetch attachment for ${item.docTitle}`;
              this.disableSendEmail = false;
              this.sendingEmail = false;
              return;
            }
          }
        }
      }

      if (this.useClip && this.clipBlob) {
        const clipBlobAsPdf = new File([this.clipBlob], 'clip.pdf', {
          type: 'application/pdf',
        });
        filesToEmail.push(clipBlobAsPdf);
        this.useClip = false;
        console.log('clipBlob added as PDF');
      }

      let newEmailId = null; // Track the new email ID for reply tracking

      const sendEmail = async (file = null) => {
        const formData = new FormData();
        formData.append('recipients', this.quickEmailTo);
        formData.append('subject', this.quickEmailSubject);
        formData.append('body', this.markedBody);

        if (file) {
          formData.append('attachment', file);
        }

        if (this.includeBcc) {
          formData.append('bcc', 'scan_documents@bigpropertymanagement.com');
        }

        this.appendInlineImagesToFormData(formData);

        try {
          const response = await axios.post('/api/send-email', formData);
          // Capture the new email ID from the response
          if (response.data && response.data.emailId) {
            newEmailId = response.data.emailId;
          }
          if (file) {
            this.getIdfromEmail(this.quickEmailTo);
            this.updateFooterMessage(
              `Email sent with attachment: ${file.name}`,
            );
          } else {
            this.getIdfromEmail(this.quickEmailTo);
            this.updateFooterMessage(
              'Email sent successfully without attachments',
            );
          }
        } catch (error) {
          const failMessage = file
            ? `Failed to send email with attachment: ${file.name}`
            : 'Failed to send email without attachments';
          console.error(failMessage, error);
          this.updateFooterMessage(failMessage);
          throw error;
        }
      };

      try {
        if (filesToEmail.length > 0) {
          for (const file of filesToEmail) {
            await sendEmail(file);
          }
        } else {
          await sendEmail();
        }

        this.updateFooterMessage('All emails sent successfully');

        // Track sent/replied: for replies, update the original email; for new emails, update the new email itself
        if (this.replyingToEmailId) {
          // This is a reply - track on the original email with the reply's ID
          await this.trackEmailSentOrReply(this.replyingToEmailId, newEmailId);
        } else if (newEmailId) {
          // This is an original send - track on the new email itself
          await this.trackEmailSentOrReply(newEmailId, null);
        }

        this.disableSendEmail = false;
        this.sendingEmail = false;

        // Close the compose popup after successful send
        this.clearComposeDraft();
        this.clearInlineImages();
        this.closeComposePopup();
      } catch (error) {
        this.disableSendEmail = false;
        this.sendingEmail = false;
        return;
      }

      this.quickEmailTo = this.quickEmailSubject = this.quickEmailBody = '';
    },

    async trackEmailSentOrReply(emailId, replyEmailId = null) {
      try {
        const response = await axios.post('/api/email/track-sent-or-reply', {
          emailId: emailId,
          replyEmailId: replyEmailId,
        });

        if (response.data) {
          // Update the local email data
          const email = this.recentEmails.find((e) => e.id === emailId);
          if (email) {
            email.repliedOrSentBy = response.data.repliedOrSentBy || '';
            if (response.data.replyId) {
              email.replyId = response.data.replyId;
            }
          }
        }
      } catch (error) {
        console.error('Error tracking email sent/reply:', error);
      }
    },

    updateFooterMessage(message) {
      eventBus.emit('footerMessage', message);
    },
    async fetchAttachmentFromList() {
      this.showLedgerListPopup = true;
      this.useSelectedAttachments = true;
      await this.fetchLedgerList();
    },
    async fetchAttachmentFromClip() {
      this.useClip = !this.useClip;

      if (!this.useClip) return;

      const request = indexedDB.open('pdfDatabase', 1);

      request.onsuccess = async (event) => {
        const db = event.target.result;
        const transaction = db.transaction(['pdfs'], 'readonly');
        const store = transaction.objectStore('pdfs');

        const getRequest = store.get('latestPDF');

        getRequest.onsuccess = async () => {
          const record = getRequest.result;
          if (record && record.blob) {
            try {
              this.clipBlob = record.blob;
            } catch (error) {
              console.error('Failed to fetch or use clipBlob:', error);
            }
          } else {
            this.useClip = false;
          }
        };

        getRequest.onerror = () => {
          console.error('Failed to retrieve clipBlob from IndexedDB');
          this.useClip = false;
        };
      };

      request.onerror = () => {
        console.error('Error opening IndexedDB');
        this.useClip = false;
      };
    },
    async fetchLedgerList() {
      try {
        const response = await axios.get('/api/fetch-ledgerlist');
        this.ledgerList = response.data.map((item) => ({
          ...item,
          attach: true,
        }));
      } catch (error) {
        console.error('Failed to fetch ledger list:', error);
      }
    },
    async viewLedgerItem(id) {
      this.openAttachmentViewerShell({ title: 'Ledger Item' });
      try {
        const response = await axios.get(`/api/fetch-ledgerlist-blob/${id}`, {
          responseType: 'blob',
        });

        const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
        this.attachmentViewerItems = [];
        this.openAttachmentInViewer(pdfBlob, 'Ledger Item');
      } catch (error) {
        this.attachmentViewerLoading = false;
        console.error('Failed to view ledger item:', error);
      }
    },
    bringToFront(event) {
      const targetElement = event.target.closest('.bring-to-frontable');

      if (targetElement) {
        const allElements = Array.from(
          document.querySelectorAll('.bring-to-frontable'),
        );
        const maxZIndex = allElements.reduce((max, el) => {
          const zIndex = parseInt(window.getComputedStyle(el).zIndex) || 0;
          return Math.max(max, zIndex);
        }, 0);

        targetElement.style.zIndex = maxZIndex + 1;
      }
    },
    setPopupPosition(event) {
      if (this.currentPopup) {
        this.popupPosition.x = event.clientX - this.currentIconOffset.x;
        this.popupPosition.y = event.clientY - this.currentIconOffset.y;

        this.currentPopup.style.left = `${this.popupPosition.x}px`;
        this.currentPopup.style.top = `${this.popupPosition.y}px`;

        this.isDragMode = false;
        document.removeEventListener('click', this.setPopupPosition);
      }
    },
    useAttachments() {
      this.showLedgerListPopup = false;
      this.useSelectedAttachments = true;
    },
    toggleDragMode(event) {
      this.isDragMode = !this.isDragMode;

      if (this.isDragMode) {
        const iconElement = event.target;
        const popupContainer = iconElement.closest('.draggable-popup');

        if (popupContainer) {
          this.currentPopup = popupContainer;

          const iconRect = iconElement.getBoundingClientRect();
          const popupRect = popupContainer.getBoundingClientRect();

          this.currentIconOffset.x = iconRect.left - popupRect.left;
          this.currentIconOffset.y = iconRect.top - popupRect.top;

          document.addEventListener('click', this.setPopupPosition);
        }
      } else {
        document.removeEventListener('click', this.setPopupPosition);
      }
    },
    emailBodyPlainText(email) {
      let rawBody = email?.body || '';
      const replyPatterns = [
        /On\s+.{10,80}\s+wrote:/i,
        /-----\s*Original Message\s*-----/i,
        /From:\s*.+\nSent:\s*/i,
        /_{10,}/,
        /<div class="gmail_quote"/i,
        /Begin forwarded message:/i,
      ];

      for (const pattern of replyPatterns) {
        const match = rawBody.search(pattern);
        if (match > 0) {
          rawBody = rawBody.substring(0, match);
          break;
        }
      }

      return rawBody
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/p>/gi, '\n')
        .replace(/<\/div>/gi, '\n')
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/\uFEFF/g, '')
        // eslint-disable-next-line no-control-regex
        .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '')
        .replace(/\u00E2\u0080\u0099/g, '\u2019')
        .replace(/\u00E2\u0080\u009C/g, '\u201C')
        .replace(/\u00E2\u0080\u009D/g, '\u201D')
        .replace(/\u00E2\u0080\u0094/g, '\u2014')
        .replace(/\u00E2\u0080\u0093/g, '\u2013')
        .replace(/\u00C2\u00A0/g, ' ')
        .replace(/\u00C3\u00A9/g, '\u00E9')
        .replace(/\u00EF\u00BB\u00BF/g, '')
        .split('\n')
        .map((line) => line.trimStart())
        .join('\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
    },

    async saveEmailToNote(email) {
      if (!email) return;
      this.pendingSaveEmail = email;
      this.showSaveToNoteConfirm = true;
    },

    async openSendToChatPicker(email) {
      if (!email) return;
      this.chatPickerEmail = email;
      this.chatPickerQuery = '';
      this.chatPickerResults = [];
      this.chatPickerTab = 'direct';
      this.chatPickerError = '';
      this.chatPickerSending = false;
      this.showSendToChatPicker = true;
      if (!this.chatPickerUsers.length) {
        try {
          const response = await axios.get('/api/get-users-tenants');
          this.chatPickerUsers = response.data || [];
        } catch (error) {
          console.error('Error loading chat recipients:', error);
          this.chatPickerError = 'Could not load chat recipients.';
        }
      }
      this.$nextTick(() => {
        if (this.$refs.chatPickerSearch) {
          this.$refs.chatPickerSearch.focus();
        }
      });
    },

    closeSendToChatPicker() {
      if (this.chatPickerSending) return;
      this.showSendToChatPicker = false;
      this.chatPickerEmail = null;
      this.chatPickerQuery = '';
      this.chatPickerResults = [];
      this.chatPickerError = '';
    },

    onChatPickerSearch() {
      const query = this.chatPickerQuery.trim().toLowerCase();
      if (!query) {
        this.chatPickerResults = [];
        return;
      }
      this.chatPickerResults = this.chatPickerUsers
        .filter((user) => {
          const name = `${user.displayName || ''} ${user.fullName || ''}`;
          return name.toLowerCase().includes(query);
        })
        .slice(0, 8);
    },

    async selectChatPickerGroupTab() {
      this.chatPickerTab = 'group';
      if (this.chatPickerGroups.length || this.chatPickerGroupsLoading) return;
      this.chatPickerGroupsLoading = true;
      try {
        const response = await axios.get('/api/chat-groups');
        this.chatPickerGroups = response.data || [];
      } catch (error) {
        console.error('Error loading chat groups:', error);
        this.chatPickerError = 'Could not load groups.';
      } finally {
        this.chatPickerGroupsLoading = false;
      }
    },

    async getChatFromUserId() {
      if (this.userId) return this.userId;
      if (this.chatFromUserId) return this.chatFromUserId;
      const response = await axios.get('/api/users');
      const you = (response.data || []).find((user) => user.you === 1);
      this.chatFromUserId = you?.id || null;
      return this.chatFromUserId;
    },

    async sendEmailBodyToChat(recipient) {
      if (this.chatPickerSending) return;
      const email = this.chatPickerEmail;
      const plainText = this.emailBodyPlainText(email);
      if (!plainText) {
        this.chatPickerError = 'No email body content to send.';
        return;
      }

      const message = email.subject
        ? `${email.subject}\n\n${plainText}`
        : plainText;

      this.chatPickerSending = true;
      this.chatPickerError = '';
      try {
        const fromUserId = await this.getChatFromUserId();
        const isTenant = recipient.peopleId && !recipient.userId;
        const isGroup = !!recipient.groupId;

        if (isTenant) {
          await axios.post('/api/tenant-chat/messages', {
            peopleId: recipient.peopleId,
            message,
          });
        } else {
          await axios.post('/api/chat-messages', {
            datetime: new Date().toISOString(),
            message,
            isReply: false,
            fromUserId,
            toUserId: isGroup ? null : recipient.userId,
            fromPeopleId: null,
            toPeopleId: null,
            toGroupId: isGroup ? recipient.groupId : null,
            isReplyTo: null,
            status: null,
            createdBy: fromUserId,
          });
        }

        const name =
          recipient.displayName ||
          recipient.fullName ||
          recipient.groupName ||
          'chat';
        this.$emit('showMessage', `Email sent to Inside Chat (${name})`);
        this.chatPickerSending = false;
        this.closeSendToChatPicker();
      } catch (error) {
        console.error('Error sending email to chat:', error);
        this.chatPickerSending = false;
        this.chatPickerError =
          error.response?.data?.error || 'Failed to send to Inside Chat.';
      }
    },

    async confirmSaveEmailToNote() {
      const email = this.pendingSaveEmail;
      this.showSaveToNoteConfirm = false;
      this.pendingSaveEmail = null;
      if (!email) return;

      try {
        const plainText = this.emailBodyPlainText(email);

        if (!plainText) {
          alert('No email body content to save.');
          return;
        }

        const noteData = {
          noteType: 'office',
          noteTitle: email.subject || 'Email Note',
          noteBody: plainText,
        };

        const response = await axios.post('/api/note', noteData);
        if (response.data) {
          this.$emit('showMessage', 'Email saved to Office Notes');
        }
      } catch (error) {
        console.error('Error saving email to note:', error);
        alert('Failed to save email to Office Notes');
      }
    },

    printEmail(email) {
      if (!email) return;

      const printWindow = window.open('', '_blank');
      const emailContent = this.recentBody(email.body);
      const fromEmail = email.from ? email.from.replace(/<[^>]*>/g, '') : '';
      const toEmail = email.to ? email.to.replace(/<[^>]*>/g, '') : '';

      printWindow.document.write(`
        <html>
          <head>
            <title>${email.subject || 'Email'}</title>
            <style>
              body {
                font-family: Arial, sans-serif;
                line-height: 1.6;
                margin: 0;
                padding: 0;
                color: #333;
              }
              @media print {
                body {
                  margin-top: 0.5in;
                  -webkit-print-color-adjust: exact;
                  print-color-adjust: exact;
                }
                @page {
                  margin-top: 0.5in;
                }
              }
              .email-header {
                border-bottom: 2px solid #3498db;
                padding-bottom: 15px;
                margin-bottom: 20px;
              }
              .email-meta {
                margin-bottom: 8px;
                font-size: 14px;
              }
              .email-meta strong {
                color: #2c3e50;
                min-width: 60px;
                display: inline-block;
              }
              .email-subject {
                font-size: 18px;
                font-weight: bold;
                color: #2c3e50;
                margin-bottom: 10px;
              }
              .email-content {
                font-size: 14px;
                margin-top: 20px;
              }
              table {
                border-collapse: collapse;
                width: 100%;
                margin: 15px 0;
              }
              table th, table td {
                border: 1px solid #ddd;
                padding: 8px;
                text-align: left;
              }
              table th {
                background-color: #f2f2f2;
                font-weight: bold;
              }
            </style>
          </head>
          <body onload="window.print(); window.close();">
            <div class="email-header">
              ${toEmail ? `<div class="email-meta"><strong>To:</strong> ${toEmail}</div>` : ''}
              ${fromEmail ? `<div class="email-meta"><strong>From:</strong> ${fromEmail}</div>` : ''}
              <div class="email-subject">${email.subject || 'No Subject'}</div>
              <div class="email-meta"><strong>Date:</strong> ${this.formatDate(email.sentOn)}</div>
            </div>
            <div class="email-content">${emailContent}</div>
          </body>
        </html>
      `);
      printWindow.document.close();
    },
    async deleteEmail(id) {
      try {
        await axios.delete(`/api/delete-email/${id}`);
        this.fetchRecentCommunications();
      } catch (error) {
        console.error('Error deleting email:', error);
        this.updateFooterMessage(
          'An error occurred while trying to delete the email. Please try again.',
        );
      }
    },
    openForwardPopup(email) {
      this.forwardingEmail = email;
      this.forwardToAddress = 'scan_documents@bigpropertymanagement.com';
      this.forwardSending = false;
      this.forwardError = '';
      this.showForwardPopup = true;
      this.$nextTick(() => {
        if (this.$refs.forwardToInput) {
          this.$refs.forwardToInput.focus();
          this.$refs.forwardToInput.select();
        }
      });
    },
    closeForwardPopup() {
      if (this.forwardSending) return;
      this.showForwardPopup = false;
      this.forwardingEmail = null;
      this.forwardToAddress = 'scan_documents@bigpropertymanagement.com';
      this.forwardError = '';
    },
    buildForwardBody(email) {
      const from = email.from ? email.from.replace(/<[^>]*>/g, '') : '';
      const to = email.to ? email.to.replace(/<[^>]*>/g, '') : '';
      const date = this.formatDate(email.sentOn, true);
      const subject = email.subject || '(no subject)';
      const body = email.body || '';

      return `<br><br>---------- Forwarded message ----------<br>
<strong>From:</strong> ${from}<br>
<strong>To:</strong> ${to}<br>
<strong>Date:</strong> ${date}<br>
<strong>Subject:</strong> ${subject}<br><br>
${body}`;
    },
    async sendForwardEmail() {
      if (!this.forwardingEmail) return;

      const recipient = this.forwardToAddress.trim();
      if (!recipient) {
        this.forwardError = 'Please enter an email address.';
        return;
      }

      if (!recipient.includes('@')) {
        this.forwardError = 'Please enter a valid email address.';
        return;
      }

      const email = this.forwardingEmail;
      const subject = (email.subject || '').trim().startsWith('Fwd:')
        ? email.subject
        : `Fwd: ${email.subject || '(no subject)'}`;

      this.forwardSending = true;
      this.forwardError = '';

      const formData = new FormData();
      formData.append('recipients', recipient);
      formData.append('subject', subject);
      formData.append('body', this.buildForwardBody(email));

      try {
        await axios.post('/api/send-email', formData);
        this.updateFooterMessage(`Email forwarded to ${recipient}`);
        this.forwardSending = false;
        this.showForwardPopup = false;
        this.forwardingEmail = null;
        this.forwardToAddress = 'scan_documents@bigpropertymanagement.com';
        this.forwardError = '';
      } catch (error) {
        console.error('Error forwarding email:', error);
        this.forwardSending = false;
        this.forwardError = 'Failed to forward email. Please try again.';
      }
    },
    openComposePopup(email = null) {
      if (email) {
        // Track which email we're replying to
        this.replyingToEmailId = email.id;

        // Check if email is from communications@bigpropertymanagement.com and has a valid 'to' field
        if (
          email.from &&
          email.from.includes('communications@bigpropertymanagement.com') &&
          email.to
        ) {
          this.quickEmailTo = email.to.replace(/<[^>]*>/g, '');
        } else {
          this.quickEmailTo = email.from
            ? email.from.replace(/<[^>]*>/g, '')
            : '';
        }
        this.quickEmailSubject = 'Re: ' + (email.subject || '');

        const strippedBody =
          email.body?.replace(/<(?!br\s*\/?)[^>]+>/g, '') || '';
        const sanitizedBody = strippedBody.replace(/[^\x20-\x7E\r\n<>/]/g, '');
        this.clearInlineImages();
        this.quickEmailBody = '\n<br><hr><br>' + sanitizedBody;

        this.updatePreview();
      } else {
        // Not replying to any email
        this.replyingToEmailId = null;

        // When compose is clicked without a specific email, populate from search input
        if (this.emailFilter && this.emailFilter.trim() !== '') {
          // Convert pipe-separated search terms back to comma-separated email addresses
          const emailsFromSearch = this.emailFilter
            .split('|')
            .map((term) => term.trim())
            .filter((term) => term && term.includes('@'))
            .join(', ');

          this.quickEmailTo = emailsFromSearch;
        } else {
          this.quickEmailTo = '';
        }
        this.quickEmailSubject = '';
        this.clearInlineImages();
        this.quickEmailBody = '';
      }

      this.showComposePopup = true;

      this.$nextTick(() => {
        if (this.$refs.quickEmailSubject) {
          this.$refs.quickEmailSubject.focus();
        }
      });
    },
    scheduleComposeDraftSave() {
      if (this.skipComposeDraftSave) return;
      clearTimeout(this.composeDraftTimer);
      this.composeDraftTimer = setTimeout(() => {
        this.persistComposeDraft();
      }, 400);
    },

    persistComposeDraft() {
      if (this.skipComposeDraftSave) return;
      const to = this.quickEmailTo || '';
      const subject = this.quickEmailSubject || '';
      const body = this.quickEmailBody || '';
      if (!to.trim() && !subject.trim() && !body.trim()) {
        clearQuickEmailDraft();
        return;
      }
      const pruned = pruneInlineImages(this.inlineImages, body);
      this.revokeUnusedInlineImages(pruned);
      this.inlineImages = pruned;
      saveQuickEmailDraft({
        to,
        subject,
        body,
        footer: this.quickEmailFooter || '',
        includeBcc: this.includeBcc,
        replyingToEmailId: this.replyingToEmailId,
        inlineImages: pruned.map((img) => ({
          cid: img.cid,
          mime: img.mime,
          dataUrl: img.dataUrl,
        })),
      });
    },

    clearComposeDraft() {
      this.skipComposeDraftSave = true;
      clearTimeout(this.composeDraftTimer);
      clearQuickEmailDraft();
      this.$nextTick(() => {
        this.skipComposeDraftSave = false;
      });
    },

    closeComposePopup() {
      this.showComposePopup = false;
      this.quickEmailTo = '';
      this.quickEmailSubject = '';
      this.quickEmailBody = '';
      this.selectedFiles = [];
      this.useSelectedAttachments = false;
      this.disableSendEmail = false;
      this.replyingToEmailId = null;
      this.clearInlineImages();
    },
    setEmailTemplate(templateType, params = null) {
      // Use passed params or fall back to stored templateParams
      const templateParams = params || this.templateParams || {};

      const templates = {
        permission: {
          subject: 'maintenance permission',
          body: 'Maintenance needs permission to enter your unit to work on your maintenance items. Can they enter in your absence, or will you be home today?',
        },
        leaseEnd: {
          subject: 'your lease is ending',
          body: 'Your lease is ending soon. Please confirm that you will be vacating the property on or before the end date.',
        },
        moveOut: {
          subject: 'Move out notice',
          body: 'Thank you for the heads up. Per your lease, we just need notification (which this email from you provides) of your intent to vacate and an approximate move out date.<br><br>If you have utilities in your name be sure to disconnect those before you leave.<br><br>Also be sure to give us a forwarding address for your deposit refund. The check will be made out in the name of both of you.<br><br>Thank you for being a tenant of Pixie Properties and we wish you well wherever life takes you next.',
        },
        resignLease: {
          subject: 'Lease renewal',
          // eslint-disable-next-line quotes
          body: "([address])<br><br>Dear Tenants,<br><br>We hope all is well with you! In [year], we will be making adjustments to how trash collection is handled. We will be raising rent for your property by [amount] to [new rent] but you will no longer need to pay for trash collection in exchange.<br><br>For those interested in resigning leases: signing early and keeping $250 extra dollars in your pocket is a great way to make [year] awesome.  We are reaching out to you to get the ball rolling early on leasing season.<br><br>Re-sign for the next year early, and you can take advantage of:<ul><li>$250 credit off first month's rent of the new lease if you re-sign by January 30.</li><li>$175 credit off first month's rent of the new lease if you re-sign by February 20.</li><li>$100 credit off first month's rent of the new lease if you re-sign by March 27.</li></ul>This offer is one offer per lease. Email us back to receive a Docusign re-sign invitation. Please be aware that if you receive the discount, and then decide to leave during the year (break your lease) you will be responsible to pay back the discount.<br><br>If you will be moving at the end of your lease, please let us know. Remember you have to give us at least 30 days written notice of your intention to leave or re-sign at the end of your lease.  Also, staying past the end of your lease results in a 150% increase in your current rent. <br><br>Finally, if you would like your place painted (color = Pixie Grey), please let us know.<br><br>Have a great day!",
        },
        rentReminder: {
          subject: 'rent reminder',
          body: 'Please pay rent as soon as possible; for convenience, give the new mobile tenant app at https://tenantapp.bigtapapp.com a try.',
        },
        secDepRet: {
          subject: 'security deposit return',
          body: 'By policy, we always address all security deposit return checks with AND for each tenant on the lease.<br><br>You have two options: you can have one person sign the back of the check, and the other then deposits it; or you request a new check be issued with OR for the names.<br><br>To do that, however, we need confirmation by text or email from EACH tenant that this is acceptable, as only one person can then deposit it without the other(s).<br><br>Let us know how you want to proceed.',
        },
      };

      const template = templates[templateType];
      if (template) {
        this.quickEmailSubject = template.subject;
        let body = template.body;

        // Replace placeholders with params if provided
        // Supports [paramName] syntax in templates
        Object.keys(templateParams).forEach((key) => {
          const placeholder = new RegExp(`\\[${key}\\]`, 'gi');
          body = body.replace(placeholder, templateParams[key]);
        });

        // For resignLease template, conditionally remove trash language
        if (
          templateType === 'resignLease' &&
          (templateParams.inclTrash === 0 || templateParams.inclTrash === false)
        ) {
          // Use the year param value since placeholders were already replaced
          const year = templateParams.year || new Date().getFullYear();
          body = body.replace(
            `In ${year}, we will be making adjustments to how trash collection is handled. `,
            '',
          );
          body = body.replace(
            ' but you will no longer need to pay for trash collection in exchange',
            '',
          );
        }

        // For resignLease template, add balance message if tenant owes money or has credit
        if (templateType === 'resignLease' && templateParams.balance !== 0) {
          const balanceAmount = Math.abs(templateParams.balance).toLocaleString(
            'en-US',
            { style: 'currency', currency: 'USD' },
          );
          let balanceMessage;
          if (templateParams.balance < 0) {
            balanceMessage = ` You have a balance of ${balanceAmount} on your account, please remediate before renewing your lease.`;
          } else {
            balanceMessage = ` You have a credit of ${balanceAmount} on your account.`;
          }
          // Insert after the trash collection exchange phrase (or after the rent raise phrase if no trash)
          body = body.replace(
            ' in exchange.<br><br>For those interested',
            ` in exchange.${balanceMessage}<br><br>For those interested`,
          );
          // Also handle the case where trash language was removed
          if (!body.includes(balanceMessage)) {
            body = body.replace(
              '.<br><br>For those interested in resigning',
              `.${balanceMessage}<br><br>For those interested in resigning`,
            );
          }
        }

        this.quickEmailBody = body;
        this.updatePreview();
      }
    },
  },
};
</script>

<style scoped>
/* Email List Container (former right column) */
.email-list-container {
  box-shadow: 9px 7px 9px 1px rgba(0, 0, 0, 0.15);
  left: 200px;
  background-color: #fff;
  padding: 0;
  border-radius: 4px;
  z-index: 1000;
  width: 100%;
  max-width: 440px;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Quick Email Title Bar */
.quick-email-title {
  margin-top: 0; /* Remove negative margins */
  margin-left: 0;
  margin-right: 0;
  background: #6e5875;
  padding: 10px;
  color: #fff;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-left: 21px;
  flex: 0 0 auto; /* Don't grow or shrink */
  position: relative;
  font-size: 1.4em;
}

/* Title actions container */
.title-actions {
  display: flex;
  align-items: center;
}

/* Close button */
.close-btn {
  background: #907793;
  color: #fff;
  width: 20px;
  height: 20px;
  border: 1px solid #ccc;
  border-radius: 5px;
  cursor: pointer;
  display: flex; /* Add flexbox */
  justify-content: center; /* Center horizontally */
  align-items: center; /* Center vertically */
  margin-right: 5px; /* Add some spacing before the drag icon */
  padding: 4px;
}

/* Title buttons container */
.title-buttons {
  display: flex;
  align-items: center;
}

/* Title button */
.title-button {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 1px solid #e1dede;
  color: #eee;
  background: #907793;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  font-size: 0.7em;
  cursor: pointer;
  margin-right: 5px;
}

.quick-email-title .home {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 1px solid #e1dede;
  color: #eee;
  background: #907793;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  font-size: 0.7em;
  cursor: pointer;
  margin-left: 6px;
}

/* Draggable Icon */
.draggable-icon {
  cursor: pointer;
  color: #888;
  transition: color 0.3s;
}

.draggable-icon.active {
  color: greenyellow;
}

/* Email Content Area - Flex container */
.email-list-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 0 10px;
}

/* Recent Communications Section */
.recent-communications {
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 0;
  margin: 0;
  font-size: 0.7em;
  overflow: hidden;
  outline: none;
  /* border: 1px solid #ddd; */
}

/* Recent Title */
.recent-title {
  font-weight: bold;
  padding: 8px 4px;
  flex: 0 0 auto;
}

/* Email limit increase button */
.email-limit-increase-btn {
  background-color: #a080a8;
  color: white;
  border: none;
  border-radius: 5px;
  width: auto;
  height: 24px;
  padding: 0 8px;
  font-size: 0.7em;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 8px;
  transition: background-color 0.3s ease;
}

.email-limit-increase-btn:hover {
  background-color: #9070a0;
}

.email-sweep-btn {
  background-color: #a080a8;
  color: white;
  border: none;
  border-radius: 5px;
  height: 24px;
  padding: 0 10px;
  font-size: 0.85em;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.email-sweep-btn:hover {
  background-color: #9070a0;
}

/* Email Search Bar Section */
.email-search-bar {
  margin-bottom: 5px;
  flex: 0 0 auto;
}

/* Email field with flexbox layout */
.email-field {
  display: flex;
  gap: 0.5em;
  margin-bottom: 0.25em;
}

/* Email label (To:, From:, Subject:) - fixed width, no wrap */
.email-label {
  flex-shrink: 0;
  width: 4.5em;
  white-space: nowrap;
}

/* Email content - wraps and aligns */
.email-content {
  flex: 1;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.from-address {
  color: #888;
  font-style: italic;
  font-size: 0.9em;
}

/* Viewed by row - smaller grey text, same alignment as other fields */
.viewed-by-row {
  font-size: 0.85em;
  color: #666;
}

.viewed-by-row .email-label {
  font-weight: normal;
  width: 5.3em; /* Compensate for smaller font to align with Subject content */
}

/* Inner Wrapper - Scrollable Section */
.inner-wrapper {
  flex: 1;
  overflow-y: auto;
  scrollbar-width: none; /* Firefox */
  width: 100%;
  padding-right: 5px; /* Add some padding for content */
}

/* Hide scrollbar for Chrome, Safari and Opera */
.inner-wrapper::-webkit-scrollbar {
  display: none;
}

/* Review mode — single expanded email (search/filters remain above) */
.email-review-mode {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  width: 100%;
}

.email-review-header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  background: #a080a8;
  color: #fff;
  padding: 8px 10px;
  border-radius: 6px 6px 0 0;
}

.email-review-title {
  font-weight: 600;
  font-size: 1.05em;
  flex: 1;
  min-width: 0;
}

.email-review-counter {
  font-size: 0.9em;
  opacity: 0.95;
  white-space: nowrap;
}

.email-review-header-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.email-review-header-btn {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.55);
  background: rgba(255, 255, 255, 0.18);
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  font-size: 0.95em;
}

.email-review-header-btn:hover {
  background: rgba(255, 255, 255, 0.32);
}

.email-review-header-btn-pinned {
  background: rgba(0, 0, 0, 0.42);
  border-color: rgba(255, 255, 255, 0.9);
}

.email-review-header-btn-pinned:hover {
  background: rgba(0, 0, 0, 0.55);
}

.email-review-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 6px 2px;
  scrollbar-width: none;
}

.email-review-body::-webkit-scrollbar {
  display: none;
}

.email-review-card {
  margin-bottom: 0;
  cursor: default;
  min-height: min(60vh, 520px);
}

.email-review-card .email-body-container {
  max-height: none;
  flex: 1;
}

.email-review-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: min(40vh, 280px);
  padding: 32px 20px;
  text-align: center;
  color: #555;
  max-width: 28em;
  margin: 0 auto;
}

.email-review-empty-icon {
  font-size: 3.25rem;
  color: #d0d0d0;
  margin-bottom: 1.1rem;
  line-height: 1;
}

.email-review-empty-text {
  font-size: 1.35em;
  line-height: 1.5;
  color: #555;
}

/* Search bar top layout */
.search-bar-top {
  display: flex;
  align-items: center;
  margin-bottom: 5px;
}

/* Filter buttons row */
.filter-buttons-row {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  width: 100%;
}

/* Maintain existing styles for other elements */

.new-email-button {
  background-color: #a080a8;
  color: white;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  margin-left: 5px;
}

.new-email-button:hover {
  background-color: #9070a0;
}

.form-group {
  display: flex;
  align-items: center;
}

.form-group label {
  padding-left: 3px;
  width: 44px;
  margin-right: 5px;
  margin-left: 5px;
  text-align: left;
  font-size: 0.7em;
}

.form-group input[type='email'],
.form-group input[type='text'],
.form-group textarea {
  flex: 1;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-sizing: border-box;
  outline: none;
}

/* Quick Actions Row */
.quick-actions-row {
  display: flex;
  gap: 3px;
  margin-bottom: 8px;
  margin-left: 58px; /* Align with form inputs */
}

.quick-action-btn {
  background-color: #a080a8;
  color: white;
  border: none;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.68em;
  cursor: pointer;
  transition: background-color 0.3s ease;
  width: 22%;
}

.quick-action-btn:hover {
  background-color: #9070a0;
}

.preview {
  background: #fff;
  border-radius: 5px;
  border: 1px solid #ddd;
  padding: 5px;
  min-height: 200px;
  overflow-y: auto;
  font-size: 0.8em;
  width: 378px;
}

.preview img {
  max-width: 100%;
  height: auto;
}

/* Buttons */
.modal-content .button-wrapper {
  display: flex;
  margin-top: 30px;
}

.modal-content .list-button {
  padding: 2px;
  height: 21px;
  width: 96px;
  margin-top: -5px;
  background-color: #eee !important;
  font-family: Arial, sans-serif;
  font-size: 0.8em;
  font-weight: 500;
  border-radius: 2px;
}

.modal-content .button-wrapper button {
  flex: 1;
  width: 115px;
  padding: 4px 2px;
}

.modal-content button {
  margin-top: 15px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.85em;
  border: 1pt solid #888;
  background-color: #ccc;
  color: #333;
  margin-right: 2px;
}

.markdown-help {
  font-size: 0.8em;
  margin-left: 90px;
  margin-top: -13px;
}

.draggable-icon {
  cursor: pointer;
  color: #888;
  transition: color 0.3s;
  position: absolute;
  right: 10px;
}

.draggable-icon.active {
  color: greenyellow;
}

.ledger-list-popup {
  position: absolute;
  top: 14%;
  left: 5%;
  background-color: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 10px;
  width: 85%;
  z-index: 1000;
}

.ledger-list-title {
  margin: -10px;
  margin-bottom: 2px;
  color: #eee;
  background-color: #333;
  border-top-left-radius: 6px;
  border-top-right-radius: 6px;
  padding: 8px;
}

.ledger-list-popup .ll-button-row {
  text-align: right;
}

.ledger-list-popup button {
  margin-left: 4px;
}

.ledger-list {
  list-style-type: none;
  padding: 0;
  margin: 0;
  font-size: 0.85em;
  margin-top: 10px;
}

.ll-list-wrapper {
  height: 350px;
  overflow-y: auto;
  font-size: 0.9em;
}

.ledger-list li {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.ledger-list li:nth-child(even) {
  background: #ddd;
}

.ledger-list li span {
  flex: 1;
  text-align: left;
  padding-right: 8px;
  padding-left: 6px;
}

.ledger-list button {
  background: none;
  border: none;
  cursor: pointer;
  color: #555;
}

.ledger-list button i {
  font-size: 1.2em;
}

.ledger-list button {
  margin-top: 10px;
  padding: 5px 10px;
  cursor: pointer;
}

/* Attachment viewer — teleported to body, viewport-centered on desktop */
.attachment-viewer {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: min(92vw, 960px);
  height: min(86vh, 760px);
  border: 1px solid rgb(145, 146, 146);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background-color: #fff;
  border-radius: 5px;
  box-shadow: 8px 9px 7px 3px rgb(0 0 0 / 22%);
  z-index: 20001;
}

/* Title bar styles */
.attachment-viewer-header {
  display: grid;
  grid-template-columns: 36px 1fr auto;
  align-items: center;
  gap: 8px;
  background-color: #333;
  color: #eee;
  padding: 10px;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  cursor: move;
  user-select: none;
}

.attachment-viewer-nav-spacer {
  width: 36px;
}

.attachment-viewer-title {
  font-weight: bold;
  text-align: center;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.attachment-viewer-count {
  font-weight: 400;
  opacity: 0.85;
}

.attachment-viewer-header-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.attachment-viewer-nav-button {
  background: transparent;
  color: #eee;
  border: none;
  width: 28px;
  height: 28px;
  cursor: pointer;
  border-radius: 4px;
}

.attachment-viewer-nav-button:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.12);
}

.attachment-viewer-nav-button:disabled {
  opacity: 0.35;
  cursor: default;
}

.attachment-viewer-close-button {
  background: transparent;
  color: #eee;
  border: none;
  font-size: 16px;
  cursor: pointer;
  width: 28px;
  height: 28px;
}

.attachment-viewer-close-button:hover {
  color: #ff6666;
}

.attachment-viewer-body {
  position: relative;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #222;
}

.attachment-viewer-loading {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f3f3f3;
}

.attachment-viewer-loading .fa-spinner {
  font-size: 5em;
  color: #c0c0c0;
}

.attachment-viewer-image {
  display: block;
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
}

.attachment-viewer iframe {
  width: 100%;
  height: 100%;
  border: none;
  background: #fff;
}

.ledger-list-popup .button-wrapper {
  margin-top: 6px;
  margin-left: -4px;
}

/* Recent communications panel and button styles */
.recent-communications {
  margin-top: 9px;
  padding: 2px 9px;
  box-sizing: border-box;
  overflow: hidden;
  font-size: 0.7em;
  border-radius: 5px;
}

.recent-title {
  font-weight: bold;
  padding: 4px 4px;
}

/* Email card structure */
.email-card {
  background: #fff;
  border: 1px solid #c1bfbf;
  padding: 5px;
  margin-bottom: 3px;
  border-radius: 5px;
  -webkit-user-select: none;
  -moz-user-select: none;
  user-select: none;
  cursor: pointer;
  position: relative;
  display: flex;
  flex-direction: column;
}

/* Header section of email card */
.email-card-header {
  flex: 0 0 auto;
  margin-bottom: 5px;
  padding-right: 42px;
}

/* Email tracking info (viewed by / replied by) */
.email-tracking-info {
  margin-top: 5px;
  padding-top: 5px;
  border-top: 1px solid #eee;
}

.tracking-row {
  font-size: 0.85em;
  color: #444;
  margin-bottom: 2px;
}

.tracking-label {
  font-weight: normal;
}

.tracking-value {
  margin-left: 4px;
  color: #333;
}

.reply-link {
  cursor: pointer;
  color: #a080a8;
  font-weight: 500;
}

.reply-link:hover {
  text-decoration: underline;
}

.reply-link i {
  font-size: 0.9em;
  margin-right: 3px;
}

/* Reply content section */
.reply-content-container {
  background-color: #f5f0f7;
  border: 1px solid #d8c8dc;
  border-radius: 4px;
  margin: 8px 0;
  padding: 8px;
  font-size: 0.9em;
}

.reply-content-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  padding-bottom: 5px;
  border-bottom: 1px solid #d8c8dc;
  color: #6e5875;
}

.reply-date {
  font-size: 0.85em;
  color: #888;
}

.reply-body-content {
  max-height: 200px;
  overflow-y: auto;
  scrollbar-width: thin;
}

.loading-reply {
  color: #888;
  font-style: italic;
  padding: 10px;
  text-align: center;
}

/* Footer section of email card */
.email-card-footer {
  flex: 0 0 auto;
  position: relative;
  margin-top: 5px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.email-footer-date {
  min-width: 0;
}

.email-footer-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.email-footer-forward-btn {
  background: #ddd;
  border: none;
  border-radius: 6px;
  height: 24px;
  padding: 0 7px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  cursor: pointer;
  color: #666;
  font-size: 11px;
  flex-shrink: 0;
  white-space: nowrap;
}

.email-footer-forward-btn:hover {
  background: #cfcfcf;
  color: #a080a8;
}

.email-footer-forward-btn i {
  font-size: 12px;
}

.email-attachment-icon {
  color: #888;
  cursor: pointer;
  font-size: 1.1em;
  padding: 2px 4px;
  border-radius: 4px;
}

.email-attachment-icon:hover {
  color: #a080a8;
  background: #f0edf2;
}

.replied-indicator {
  color: #28a745;
  font-weight: 500;
  cursor: pointer;
}

.replied-indicator:hover {
  text-decoration: underline;
}

.viewed-indicator {
  color: #6c757d;
  font-weight: 500;
}

/* Scrollable body container - adjusted for expanded view */
.email-body-container {
  flex: 1 1 auto;
  overflow: hidden;
  position: relative;
  border-top: 1px solid #eee;
  border-bottom: 1px solid #eee;
  padding: 5px 0;
  margin: 5px 0;
  user-select: all !important;
  cursor: pointer;
}

/* Scrollable content */
.email-body-content {
  user-select: all !important;
  height: 100%;
  overflow-y: auto;
  scrollbar-width: none; /* Firefox */
  padding-right: 5px;
  /* Set pointer to text selection cursor */
  cursor: text !important;
}

/* Hide scrollbar for Chrome, Safari and Opera */
.email-body-content::-webkit-scrollbar {
  display: none;
}

/* Global style to target HTML content inserted via v-html */
:deep(.selectable-content *) {
  -webkit-user-select: all !important;
  -moz-user-select: all !important;
  -ms-user-select: all !important;
  user-select: all !important;
  cursor: text !important;
}

/* Using more specific class with deep selector */
:deep(.selectable-content p),
:deep(.selectable-content div),
:deep(.selectable-content span),
:deep(.selectable-content a) {
  -webkit-user-select: all !important;
  -moz-user-select: all !important;
  -ms-user-select: all !important;
  user-select: all !important;
}

/* Action icons - Only visible when not collapsed */
.email-icons {
  position: absolute;
  top: 5px;
  right: 5px;
  display: flex;
  gap: 5px;
  background: #dbdbdb;
  padding-left: 6px;
}

.email-delete,
.email-forward,
.email-print,
.email-note,
.email-chat {
  color: #888;
  cursor: pointer;
  font-size: 1.2em;
}

.email-delete:hover,
.email-forward:hover,
.email-print:hover,
.email-note:hover,
.email-chat:hover {
  color: #a080a8; /* Use the app's purple color on hover */
}
/* Expanded email card styles */
.email-card.expanded {
  flex: 1;
  min-height: 400px;
}

.email-card p {
  margin: 0;
}

.email-card:hover {
  background: #ddd;
}

.column-wrapper {
  display: flex; /* Enable Flexbox layout */
  justify-content: space-between; /* Evenly distribute columns */
  max-height: 100%;
}

.left-column {
  flex: 1;
  margin: 5px;
}

.right-column {
  flex: 1;
  margin: 5px;
  max-height: 735px;
  overflow: hidden;
}

.left-column .recent-comm-btn {
  font-size: 0.7em;
  padding: 3px;
  padding-left: 4px;
  margin-left: 91px;
  margin-bottom: 0;
  background: #d8d8d8;
  border: 1pt solid #ddd;
}

#quickEmailBody {
  height: 100px;
  width: 360px;
}

#quickEmailFooter {
  height: 50px;
  width: 360px;
  font-size: 0.8em;
}

.incoming-button {
  padding: 4px;
}

.pushed {
  background-color: #a080a8 !important;
  color: #fff !important;
}

.email-search-bar input {
  border: 1pt solid #ddd;
  width: 180px;
  border-radius: 4px;
  padding: 4px;
  outline: none;
}

.search-field {
  position: relative;
  display: flex;
  align-items: center;
  width: 180px;
  flex: 0 0 auto;
}

.email-search-bar .search-field input {
  width: 100%;
  box-sizing: border-box;
  padding-right: 22px;
}

.search-clear {
  position: absolute;
  right: 2px;
  top: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  padding: 0 4px;
  margin: 0;
  color: #666;
  cursor: pointer;
  font-size: 0.9em;
  line-height: 1;
}

.search-clear:hover {
  color: #222;
  background: transparent;
}

.action-buttons-right {
  display: flex;
  align-items: center;
  margin-left: auto;
}

.filter-button {
  font-size: 1em;
  border-radius: 5px;
  border: 1px solid #ddd;
  padding: 3px 5px;
  margin-right: 2px;
  white-space: pre-wrap;
  flex: 1;
  min-width: 0;
  height: 40px;
  max-width: 19%;
}

.email-card:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.clip-close-button {
  background: #a080a8 !important;
  margin-top: 0px !important;
  width: 100px;
}
.clip-list-button {
  background: #bd98c5 !important;
  color: #fff;
  border: 1pt solid #ddd;
  margin-top: 0px !important;
  width: 100px;
  padding: 5px;
  border-radius: 5px;
  margin-right: 2px;
}

.clip-use-button {
  border: 1pt solid #856a8b !important;
  background: #bd98c5 !important;
  margin-top: 0px !important;
  color: #eee;
  width: 100px;
}

.copy-panel {
  width: 284px;
  z-index: 9999;
  background: #fff;
  position: absolute;
  top: 50%;
  left: 50%;
  box-shadow: 8px 7px 10px 6px rgba(0, 0, 0, 0.25);
  font-size: 0.8em;
  border: 1px solid #888;
  padding: 6px;
  border-radius: 5px;
  cursor: pointer;
}

.copy-panel label {
  margin-top: 5px;
  font-weight: bold;
}

.copy-panel-header {
  margin: -6px;
  height: 18px;
  background: #333;
  color: #fff;
  padding: 5px;
  text-align: center;
  margin-bottom: 10px;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  font-size: 1.1em;
}
.copy-message {
  background: green;
  color: #fff;
  font-size: 9px;
  margin-top: 5px;
  margin-bottom: 5px;
  transition: opacity 0.5s ease-out;
  padding: 4px;
  min-height: 10px;
  height: 10px;
}

.url {
  margin: 5px;
  color: #a080a8;
  font-size: 0.9em;
  word-wrap: break-word;
  overflow-wrap: break-word;
  white-space: normal;
}

.disabled-button {
  background: #888 !important;
  cursor: not-allowed;
}

.read-email {
  color: #777777;
}

.selected-email {
  background: #d9d8d8;
  -webkit-user-select: all;
  -moz-user-select: all;
  user-select: all;
}

/* Compose Popup - updated positioning as a child */
.compose-popup {
  position: absolute;
  bottom: 12px;
  left: 3%;
  background-color: #fff;
  border-radius: 8px;
  z-index: 10;
  width: 94%;
  height: 92%;
  overflow: hidden; /* Changed from overflow-y: auto to prevent unnecessary scrolling */
  border: 1px solid #d6d6d6;
  display: flex;
  flex-direction: column;
}

/* Adjust the compose content to fit better in the new layout */
.compose-content {
  margin-top: 12px;
  padding-right: 10px;
  padding-left: 10px;
  flex: 1;
  overflow-y: auto;
  scrollbar-width: none; /* Hide scrollbar for Firefox */
  background: #eee;
  padding-top: 8px;
}

/* Fixed footer for compose popup */
.compose-footer {
  position: relative;
  bottom: 0;
  left: 0;
  width: 100%;
  background-color: #f5f5f5;
  border-top: 1px solid #ddd;
  padding: 10px;
  box-sizing: border-box;
}

.form-group {
  margin-bottom: 8px;
}

.compose-footer .form-group {
  margin-bottom: 5px;
}

.compose-footer button {
  background-color: #a080a8;
  color: #fff;
  border: none;
  padding: 5px 10px;
  border-radius: 4px;
  cursor: pointer;
}

/* Action row with buttons and markdown link */
.action-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 10px;
  margin-left: 57px;
}

/* Button wrapper */
.button-wrapper {
  display: flex;
  gap: 10px;
}

/* Markdown help link */
.markdown-help {
  font-size: 0.8em;
  text-align: right;
  margin: 0; /* Reset margins */
  align-self: center;
}

/* Hide scrollbar for Chrome, Safari and Opera */
.compose-content::-webkit-scrollbar {
  display: none;
}

/* Loading Spinner */
.loading-spinner-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
}

.loading-spinner {
  border: 3px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-top: 3px solid #a080a8;
  width: 30px;
  height: 30px;
  animation: spin 1s linear infinite;
}

.loading-text {
  margin-top: 10px;
  color: #666;
  font-size: 0.9em;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* Selectable text class */
:deep(.selectable-content) {
  -webkit-user-select: text !important;
  -moz-user-select: text !important;
  -ms-user-select: text !important;
  user-select: text !important;
  cursor: text !important;
}

/* Button spinner */
.button-spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: #fff;
  animation: spin 1s ease-in-out infinite;
}

/* Mobile-specific styles */
@media (max-width: 768px) {
  .attachment-viewer {
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: min(96vw, 440px);
    height: min(80vh, 560px);
  }
}

/* ========== EMAIL NOTES STYLES ========== */
.email-note-icon {
  position: absolute;
  top: 23px;
  right: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  background-color: #eee;
  color: #aaa;
  cursor: pointer;
  font-size: 12px;
  border: 1px solid #ddd;
  z-index: 10;
  transition: background-color 0.2s ease;
}

.email-note-icon:hover {
  background-color: #ddd;
  color: #666;
}

.email-note-icon.has-note {
  background-color: #bbb;
  color: #fff;
}

.email-note-icon.has-note:hover {
  background-color: #999;
}

.chat-picker-dialog {
  width: 92%;
  max-width: 360px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  max-height: 70vh;
}

.chat-picker-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: #333;
  color: #fff;
  border-radius: 8px 8px 0 0;
  font-weight: bold;
}

.chat-picker-search {
  margin: 10px 12px 6px;
  padding: 7px 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 1em;
}

.chat-picker-tabs {
  display: flex;
  gap: 6px;
  padding: 0 12px 8px;
}

.chat-picker-tabs button {
  flex: 1;
  border: 1px solid #ccc;
  background: #f4f4f4;
  border-radius: 4px;
  padding: 6px 8px;
  cursor: pointer;
}

.chat-picker-tabs button.active {
  background: #a080a8;
  color: #fff;
  border-color: #a080a8;
}

.chat-picker-body {
  overflow-y: auto;
  min-height: 140px;
  padding: 0 12px 12px;
}

.chat-picker-body ul {
  list-style: none;
  margin: 0;
  padding: 0;
}

.chat-picker-body li {
  padding: 8px 6px;
  cursor: pointer;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  gap: 8px;
}

.chat-picker-body li:hover {
  background: #f0e8f3;
}

.chat-picker-body li small {
  color: #888;
  font-size: 0.8em;
}

.chat-picker-empty {
  color: #777;
  font-size: 0.9em;
  padding: 12px 4px;
}

.chat-picker-status,
.chat-picker-error {
  padding: 0 12px 10px;
  font-size: 0.85em;
}

.chat-picker-error {
  color: #b00020;
}

.email-note-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
}

.email-note-popup {
  width: 95vw;
  max-width: 600px;
  height: 80vh;
  max-height: 500px;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  font-size: inherit;
}

.email-note-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background-color: #333;
  color: #fff;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  font-weight: bold;
}

.email-note-close {
  cursor: pointer;
  padding: 4px;
}

.email-note-close:hover {
  color: #ff6666;
}

.email-note-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 10px;
  gap: 10px;
}

.email-note-edit-pane {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.email-note-textarea {
  flex: 1;
  width: 100%;
  resize: none;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-family: monospace;
  font-size: inherit;
  box-sizing: border-box;
}

.email-note-preview-pane {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  border: 1px solid #eee;
  border-radius: 4px;
  background-color: #fafafa;
}

.email-note-preview-label {
  padding: 4px 8px;
  font-size: 0.85em;
  color: #666;
  background-color: #eee;
  border-bottom: 1px solid #ddd;
}

.email-note-preview {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
  font-size: inherit;
  line-height: 1.4;
}

.email-note-view-pane {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow-y: auto;
}

.email-note-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 8px 12px;
  border-top: 1px solid #eee;
  background-color: #f9f9f9;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
}

.email-note-btn {
  padding: 4px 12px;
  font-size: 0.85em;
  border: 1px solid #bbb;
  border-radius: 4px;
  cursor: pointer;
  background-color: #e0e0e0;
  color: #333;
  transition: background-color 0.2s ease;
}

.email-note-btn:hover:not(:disabled) {
  background-color: #bdbdbd;
}

.email-note-btn:disabled {
  background-color: #f5f5f5;
  color: #bbb;
  border-color: #ddd;
  cursor: not-allowed;
}

/* Note Delete Confirmation Dialog */
.note-confirm-dialog {
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  padding: 20px;
  min-width: 250px;
  text-align: center;
}

.note-confirm-message {
  font-size: 1em;
  margin-bottom: 16px;
  color: #333;
}

.note-confirm-buttons {
  display: flex;
  justify-content: center;
  gap: 12px;
}

/* Forward Email Popup */
.forward-popup-dialog {
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  padding: 20px;
  min-width: 320px;
  max-width: 90vw;
}

.forward-popup-title {
  font-size: 1em;
  font-weight: 600;
  margin-bottom: 12px;
  color: #333;
}

.forward-popup-label {
  display: block;
  font-size: 0.9em;
  color: #555;
  margin-bottom: 6px;
}

.forward-popup-input {
  width: 100%;
  box-sizing: border-box;
  padding: 8px 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 0.95em;
  margin-bottom: 12px;
}

.forward-popup-input:focus {
  outline: none;
  border-color: #a080a8;
}

.forward-popup-input:disabled {
  background: #f5f5f5;
  color: #888;
}

.forward-popup-error {
  color: #c0392b;
  font-size: 0.85em;
  margin-bottom: 10px;
}

.forward-popup-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.forward-popup-send-btn {
  min-width: 64px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.forward-popup-spinner {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid rgba(0, 0, 0, 0.15);
  border-radius: 50%;
  border-top-color: #666;
  animation: spin 1s ease-in-out infinite;
}
</style>
